/* THE §5 PROGRESSION RULES, TWIN OF `HybridTrainer/Services/AI/V6Progression.swift` (2,123 lines).
 *
 * 🔴 PORTED IN SLICES, ONE FAMILY PER COMMIT, AND THIS FILE SAYS WHERE IT HAS GOT TO.
 * `SLICE_STATE` below is the machine-readable answer and it is not decoration: a caller that ran
 * an unported rule would produce a plausible prescription from nothing, so the orchestrator (a
 * later slice) refuses on any rule this file has not reached. **A twin that guesses is worse than
 * no twin**, which is the whole argument for the seam in `progression.ts` having no default.
 *
 * SLICE 1 (this commit): the PRIMITIVES every rule stands on, and nothing that decides.
 *   · `RowConfig` — the read-THROUGH view over `progression_config`
 *   · `BandLadder` — the ordered assistance ladder
 *   · `ProgramConfig` — waves, emphasis, the straight-arm budget, `declaresDeload`
 *   · `performance` — §5's shared completion test, with `perSetTarget` / `perSetLoadTarget`
 *   · the `Rule` and `ChangeKind` vocabularies, complete
 *
 * ⚠️ WHY THE PRIMITIVES ALONE, RATHER THAN "THE ORCHESTRATOR PLUS THE FIRST RULE". An
 * orchestrator whose other branches are missing is a half-orchestrator that SILENTLY SKIPS them,
 * and every rule it skips looks like a rule that decided to hold. Ported this way each slice is
 * complete in itself and nothing can run until the branch that would run it exists.
 *
 * ⚠️ AND THE NUMERIC AND STRING PRIMITIVES WERE TRANSCRIBED FIRST, IN `progression.ts`, ON
 * PURPOSE — round-half-away-from-zero, `toFixed` as an equality key, uuid case. They are the
 * floor everything here stands on and every one of them looks like a detail until it is a wrong
 * weight on somebody's bar (`docs/TRAP-CODEX.md`, measurement-discipline).
 */

import type { LoggedSet as CoachLoggedSet } from "./progression.ts";
/* ⚠️ A VALUE IMPORT, AND DELIBERATELY SO. `snappedKg` below used to open-code
   round-half-away-from-zero a second time, which is this repository's defining failure in
   miniature: two statements of one numeric rule, in two files, with nothing that would go red if
   one of them drifted. `progression.ts` already exports it with the reasoning attached
   (`Double.rounded()` is half-away-from-zero, `Math.round` is half-UP, and they disagree on
   exactly the negative halves), so this file asks IT rather than restating it. Both `snappedKg`
   and §5.13's `redose` now stand on the one definition. */
import { roundHalfAwayFromZero } from "./progression.ts";

/** Which families have a twin. ⚠️ MACHINE-READABLE AND LOAD-BEARING — the orchestrator will read
 *  it to refuse, so it is a list rather than a comment. */
export const SLICE_STATE = {
  portedRules: ["marker", "barbell_linear", "weighted_bw_biweekly", "rep_ladder",
                "hold_rung", "distance_cm", "box_height_cm", "accessory_double"] as Rule[],
  /** The POST-PASSES that have a twin — `portedRules`' opposite number, read by `portGaps` the
   *  same way and for the same reason.
   *
   *  ⚠️ IT EXISTS BECAUSE THE LAST RULE FAMILY LANDING LEFT THE GATE WITH NOTHING TO DERIVE FROM.
   *  While families were missing, `portGaps` could ask `portedRules` and be right by construction;
   *  the post-passes had no such list, so each one was refused by a hand-written clause that a
   *  slice could remove while forgetting its code, or implement while forgetting its clause. One
   *  list closes both directions: a pass whose entry is missing can never run, and an entry whose
   *  code is missing refuses nothing and is caught by the pass's own tests failing.
   *
   *  ⚠️ `testedMaxSeconds` IS NOT A MEMBER AND MUST NOT BECOME ONE WITH §5.13. It is the input to
   *  `doseBandNote` (V6Progression.swift:1983), which reports a dose sitting outside the 60–75%
   *  band — a different function from `redose`, not ported, and reading the map the CALLER passes
   *  in rather than the one this week just measured. */
  portedFeatures: ["test_max", "gate", "wristPainReported", "painfulRegions",
                   "straight_arm_budget", "target_sets", "testedMaxSeconds"] as readonly string[],
  /** Every family, in the order `V6Progression.Rule` declares them. */
  allRules: [
    "barbell_linear", "weighted_bw_biweekly", "rep_ladder", "hold_rung",
    "accessory_double", "distance_cm", "box_height_cm", "marker",
  ] as const,
} as const;

/** Mirrors `V6Progression.Rule`, V6Progression.swift:39. Each case's §-reference is the Swift's. */
export type Rule =
  | "barbell_linear"        // §5.1  barbell lower/hinge: +step while every set is at or above the RIR bar
  | "weighted_bw_biweekly"  // §5.2  weighted bodyweight: +step every `every_weeks` clean weeks
  | "rep_ladder"            // §5.3  rep-based skills: +1 rep to ONE set a week
  | "hold_rung"             // §5.4  static holds: seconds NEVER lengthen inside a rung
  | "accessory_double"      // §5.11 hypertrophy accessories: double progression inside a rep range
  | "distance_cm"           // §5.9  a lean, a gap, a reach — a DISTANCE the row's own note states
  | "box_height_cm"         // §5.10 a discrete ladder of heights, gated on a quality judgement
  | "marker";               // §3    logged, never auto-progressed

export function ruleFromName(name: string): Rule | null {
  return (SLICE_STATE.allRules as readonly string[]).includes(name) ? (name as Rule) : null;
}

/** Mirrors `V6Progression.ChangeKind`, V6Progression.swift:407. Complete, and complete on
 *  purpose: a twin missing a kind would report a real change under the wrong word. */
export type ChangeKind =
  | "loadIncrease" | "loadHeld" | "repIncrease" | "ladderSwitch" | "assistanceDecrease"
  | "rungAdopted" | "setAdded" | "setsHalved" | "setsRestored" | "frozen" | "refusedByBudget"
  | "blocked" | "unhandledRule" | "doseOutOfBand" | "distanceIncrease" | "heightIncrease"
  | "gateArmed" | "rungIntroduced" | "rungSlotConverted" | "rehabRamp" | "rehabFlare"
  | "rehabBlocked" | "painFreeze" | "holdRedosed";

// ── JSON, read the way the Swift reads it ─────────────────────────────────────────────────────

export type Json = null | boolean | number | string | Json[] | { [k: string]: Json };

/* ⚠️ THE ACCESSORS ARE DELIBERATELY NARROW, BECAUSE `JSONValue`'s ARE. `intValue` on a Swift
   `JSONValue` answers for a number and nothing else, so a config holding `"3"` as a STRING reads
   as absent on both sides. A twin that helpfully parsed the string would silently accept a config
   the Swift rejects, and the two would then disagree about a stored rule with nothing red. */
const asNumber = (v: Json | undefined): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;
const asText = (v: Json | undefined): string | null => (typeof v === "string" ? v : null);

/** Mirrors `V6Progression.RowConfig`, V6Progression.swift:73.
 *
 *  ⚠️ IT READS **THROUGH** THE STORED JSON, NEVER INSTEAD OF IT (V6-2's finding). This column
 *  carries the state a rule hands itself week after week — a consecutive-fail count, a hold
 *  counter, the sets a deload took away — so `raw` is kept WHOLE and every write MERGES into it.
 *  A typed struct that re-encoded only the keys it recognised would silently RESET a progression,
 *  visible only as a program that quietly stopped advancing. */
export class RowConfig {
  raw: Record<string, Json>;

  private constructor(raw: Record<string, Json>) {
    this.raw = raw;
  }

  /** Mirrors `init?(_ value: JSONValue?)` — null for anything that is not an object, which is
   *  what "this row has no rule" means and is NOT the same as an empty rule. */
  static from(value: Json | undefined): RowConfig | null {
    if (value === null || value === undefined) return null;
    if (typeof value !== "object" || Array.isArray(value)) return null;
    return new RowConfig({ ...(value as Record<string, Json>) });
  }

  static fromRaw(raw: Record<string, Json>): RowConfig {
    return new RowConfig({ ...raw });
  }

  /** Null for a rule string this build does not implement — REPORTED, never treated as
   *  "no rule". `ruleName` keeps the unrecognised word for the message. */
  get rule(): Rule | null { return ruleFromName(this.ruleName); }
  get ruleName(): string { return asText(this.raw["rule"]) ?? ""; }

  double(key: string): number | null { return asNumber(this.raw[key]); }
  /** ⚠️ `intValue` IN SWIFT TRUNCATES A `Double` TOWARD ZERO (`Int(d)`), so `2.9` is 2 and
   *  `-2.9` is -2. `Math.trunc` is the same function; `Math.floor` is not, and would disagree
   *  on every negative value in a config. */
  int(key: string): number | null {
    const n = asNumber(this.raw[key]);
    return n === null ? null : Math.trunc(n);
  }
  text(key: string): string | null { return asText(this.raw[key]); }
  bool(key: string): boolean | null {
    const v = this.raw[key];
    return typeof v === "boolean" ? v : null;
  }
  /** ⚠️ `compactMap` DROPS the non-numbers rather than failing, so `[3, "x", 4]` is `[3, 4]` on
   *  both sides — a shortened array, which is exactly the V7-1 shape. It is faithful, and it is
   *  the caller's business to notice a ladder shorter than its set count. */
  ints(key: string): number[] | null {
    const v = this.raw[key];
    if (!Array.isArray(v)) return null;
    return v.map(asNumber).filter((n): n is number => n !== null).map(Math.trunc);
  }

  set(key: string, value: Json | undefined): void {
    if (value === undefined) delete this.raw[key];
    else this.raw[key] = value;
  }

  /** What goes back into the column: everything that was there, plus this week's state. */
  get json(): Record<string, Json> { return { ...this.raw }; }

  /** §5 rule 4(a) — the assistance ladder as an ORDERED ENUM. Null when the row states no
   *  ladder, which is what every unassisted row means. */
  get bandLadder(): BandLadder | null {
    const v = this.raw["band_ladder"];
    if (!Array.isArray(v)) return null;
    const rungs = v.map(asText).filter((s): s is string => s !== null);
    if (rungs.length < 2) return null;
    return new BandLadder(rungs, this.int("band_index") ?? 0);
  }
}

/** Mirrors `V6Progression.BandLadder`, V6Progression.swift:126.
 *
 *  ⚠️ THE INDEX IS THE VALUE. The rungs are strings the athlete recognises and the ONLY relation
 *  the domain defines over them is their ORDER — earlier is easier, later is harder, and nothing
 *  lies between two adjacent rungs. So there is `thinner`, one position, and NO ARITHMETIC AT
 *  ALL, because there is none to offer. */
export class BandLadder {
  readonly rungs: string[];
  readonly index: number;

  /** ⚠️ CLAMPED ON CONSTRUCTION, so a stored index that has drifted past the end of a shortened
   *  ladder names the LAST rung rather than reading as "no band at all". */
  constructor(rungs: string[], index: number) {
    this.rungs = rungs;
    this.index = Math.min(Math.max(0, index), Math.max(0, rungs.length - 1));
  }

  get currentRung(): string { return this.rungs.length === 0 ? "" : (this.rungs[this.index] ?? ""); }

  /** One position harder, or null at the end. NEVER two, and never a value computed between. */
  get thinner(): { rung: string; index: number } | null {
    const next = this.index + 1;
    const rung = this.rungs[next];
    return rung === undefined ? null : { rung, index: next };
  }
}

// ── The 12-week plan, which is config and not rows ────────────────────────────────────────────

export interface Wave { weeks: number[]; emphasis: string; deloadWeeks: number[] }
export interface Budget { baseSeconds: number; buildGrowth: number; deloadFactor: number }

/** Mirrors `V6Progression.ProgramConfig`, V6Progression.swift:150. */
export class ProgramConfig {
  readonly raw: Record<string, Json>;

  constructor(value: Json | undefined) {
    this.raw = value !== null && typeof value === "object" && !Array.isArray(value)
      ? { ...(value as Record<string, Json>) } : {};
  }

  get waves(): Wave[] {
    const v = this.raw["waves"];
    if (!Array.isArray(v)) return [];
    const out: Wave[] = [];
    for (const entry of v) {
      if (entry === null || typeof entry !== "object" || Array.isArray(entry)) continue;
      const o = entry as Record<string, Json>;
      const weeksRaw = o["weeks"];
      const weeks = Array.isArray(weeksRaw)
        ? weeksRaw.map(asNumber).filter((n): n is number => n !== null).map(Math.trunc) : [];
      if (weeks.length === 0) continue;
      const deloadRaw = o["deload_weeks"];
      out.push({
        weeks,
        emphasis: asText(o["emphasis"]) ?? "",
        deloadWeeks: Array.isArray(deloadRaw)
          ? deloadRaw.map(asNumber).filter((n): n is number => n !== null).map(Math.trunc) : [],
      });
    }
    return out;
  }

  /** §4. ⚠️ EVERY NUMBER COMES FROM THE CONFIG AND NONE IS DEFAULTED — a budget this file
   *  invented would be a constraint nobody wrote down. All three or nothing. */
  get budget(): Budget | null {
    const v = this.raw["straight_arm_budget"];
    if (v === null || typeof v !== "object" || Array.isArray(v)) return null;
    const o = v as Record<string, Json>;
    const base = asNumber(o["base_seconds"]);
    const growth = asNumber(o["build_growth"]);
    const deload = asNumber(o["deload_factor"]);
    if (base === null || growth === null || deload === null) return null;
    return { baseSeconds: base, buildGrowth: growth, deloadFactor: deload };
  }

  /** The skill family this week's wave is built around — the ONE thing that unlocks §5.4's
   *  "+1 set" dimension. ⚠️ Null when the config says nothing, and **a null emphasis matches
   *  NOTHING rather than everything** — which is the unmatched-string trap stated as a return
   *  type (`docs/TRAP-CODEX.md`, vocabulary-mismatch instance 20). */
  emphasis(week: number): string | null {
    const wave = this.waves.find((w) => w.weeks.includes(week));
    if (wave === undefined) return null;
    return wave.emphasis === "" ? null : wave.emphasis;
  }

  /** §4's trajectory, walked from week 1. Mirrors `straightArmAllowanceSeconds`,
   *  V6Progression.swift:210.
   *
   *  🔴 A DELOAD IS A FRACTION OF THE LAST **BUILD** WEEK, NOT OF THE WEEK BEFORE IT — so it
   *  cannot compound, and the build weeks after it resume from where they were. Reproduces the
   *  spec's own arithmetic: 136 → 149.6 → 164.56 → 98.736 (deload) → 181.016, which is the pin
   *  this function is checked against rather than a shape anybody chose. */
  straightArmAllowanceSeconds(week: number): number | null {
    const budget = this.budget;
    if (budget === null || week < 1) return null;
    let current = budget.baseSeconds;
    let lastBuild = budget.baseSeconds;
    if (week <= 1) return current;
    for (let w = 2; w <= week; w++) {
      if (this.declaresDeload(w) === true) {
        current = lastBuild * budget.deloadFactor;
      } else {
        current = lastBuild * (1 + budget.buildGrowth);
        lastBuild = current;
      }
    }
    return current;
  }

  /** What the WAVE LAYOUT says about a week. ⚠️ Null means the config does not describe that week
   *  at all — DIFFERENT from "it is a build week", which is why the caller's own `isDeload`, read
   *  from the phase the athlete actually sees, stays the authority and a disagreement is
   *  REPORTED rather than resolved silently. */
  declaresDeload(week: number): boolean | null {
    const wave = this.waves.find((w) => w.weeks.includes(week));
    if (wave === undefined) return null;
    return wave.deloadWeeks.includes(week);
  }
}

// ── §5's shared completion test ───────────────────────────────────────────────────────────────

export type Performance =
  | { kind: "clean" }
  /** No log at all for this row. ⚠️ **NOT A FAILURE** — counting it as one would let a holiday
   *  trigger §5.1's two-week load hold, so every rule freezes instead and says so. */
  | { kind: "notLogged" }
  | { kind: "missed"; why: string };

/** The V6 row shape, narrowed to what `performance` reads. */
export interface RulePrescription {
  sets: number;
  repsMin: number | null;
  perSetReps: number[];
  holdSecondsMin: number | null;
  targetWeightKg: number | null;
  perSetWeightKg: number[];
}

/** Mirrors `V6Progression.LoggedSet`, V6Progression.swift:307 — note it carries `completed` as a
 *  BOOLEAN, where the coach-side set carries a status string. The adapter is the caller's
 *  (`CoachProgression.plan` maps `status` → `completed` before handing rows over). */
export interface RuleLoggedSet {
  reps: number | null;
  holdSeconds: number | null;
  weightKg: number | null;
  rpe: number | null;
  completed: boolean;
  bandLabel: string | null;
}

/** Mirrors `LoggedSet.rir`, V6Progression.swift:330. */
export const rirOf = (s: RuleLoggedSet): number | null => (s.rpe === null ? null : 10 - s.rpe);

/** The coach-side set, adapted. Kept HERE rather than at each call site so the two vocabularies
 *  meet in exactly one place — the twenty-four-instance failure is two spellings of one thing. */
export function fromCoachLoggedSet(s: CoachLoggedSet): RuleLoggedSet {
  return {
    reps: s.reps,
    holdSeconds: s.holdSeconds,
    weightKg: s.weightKg,
    rpe: s.rpe,
    completed: s.status === "completed" || s.status === "partial",
    bandLabel: s.bandLabel,
  };
}

/** Mirrors `V6Progression.perSetTarget`, V6Progression.swift:628.
 *  ⚠️ `repsMin` RATHER THAN `repsMax`, DELIBERATELY — a range is a range, and §5.11 climbs it one
 *  rep at a time through `per_set_reps` rather than demanding its top every week. */
export function perSetTarget(p: RulePrescription, index: number): number | null {
  const fromLadder = p.perSetReps[index];
  if (fromLadder !== undefined) return fromLadder;
  return p.repsMin;
}

/** Mirrors `V6Progression.perSetLoadTarget`, V6Progression.swift:638.
 *  Null when the row names no load at all. */
export function perSetLoadTarget(p: RulePrescription, index: number): number | null {
  const fromArray = p.perSetWeightKg[index];
  if (fromArray !== undefined) return fromArray;
  return p.targetWeightKg;
}

/** Mirrors `AssistLoad.kg`, AssistLoad.swift:47 — how a load is SAID. Used inside a `missed`
 *  sentence, which is part of the contract the fixture compares, so it is transcribed rather
 *  than approximated. */
export function kgText(value: number): string {
  if (value % 1 === 0) return String(Math.trunc(value));
  let text = value.toFixed(2);
  while (text.endsWith("0")) text = text.slice(0, -1);
  if (text.endsWith(".")) text = text.slice(0, -1);
  return text;
}

/** Mirrors `V6Progression.performance`, V6Progression.swift:595.
 *
 *  A row is clean when every prescribed set was logged as completed AND reached its own target —
 *  the per-set rep target where the row states one, the rep-range FLOOR otherwise, and the
 *  prescribed seconds for a hold.
 *
 *  ⚠️ THE RIR BAR IS ONLY APPLIED WHERE THE SPEC STATES IT (§5.1's "≥2 RIR") and only to sets
 *  that actually carry an RPE. The logger's RPE is optional, so treating a missing one as "too
 *  hard" would stall a barbell lift for ever on a technicality and treating it as "easy" would
 *  invent evidence. A LOGGED RPE below the bar blocks the jump; SILENCE leaves the completion
 *  evidence to decide.
 *
 *  ⚠️ AND A COMPLETED SET BELOW ITS PRESCRIBED LOAD IS MISSED (2026-09-10). The logged weight was
 *  compared NOWHERE, so 8 @ 0.5 kg against 8 @ 1 kg read `.clean` and the ladder climbed — his
 *  Y-Raise, live, offered 8,8,9 for a week he had not done. A set naming NO weight is SILENCE,
 *  not a miss (an absent value is not a value), and a row prescribing no load asks nothing.
 *
 *  ⚠️ THE `continue`s ARE LOAD-BEARING AND ARE NOT `return`s. A hold row whose set logged no
 *  seconds, or a rep row whose set logged no reps, is UNJUDGEABLE on that axis — the Swift skips
 *  to the next set and still checks the LOAD and RIR of the ones that can be judged. A twin that
 *  returned `clean` there would stop reading the rest of the row. */
export function performance(
  p: RulePrescription,
  log: { sets: RuleLoggedSet[] } | null,
  minimumRIR: number | null = null,
): Performance {
  if (log === null || log.sets.length === 0) return { kind: "notLogged" };
  const done = log.sets.filter((s) => s.completed);
  if (done.length < p.sets) {
    return { kind: "missed", why: `only ${done.length} of ${p.sets} sets were completed` };
  }
  for (let index = 0; index < done.length; index++) {
    const set = done[index];
    if (set === undefined) continue;
    if (p.holdSecondsMin !== null) {
      const held = set.holdSeconds;
      if (held !== null) {
        if (held < p.holdSecondsMin) {
          return { kind: "missed", why: `set ${index + 1} held ${held}s of ${p.holdSecondsMin}s` };
        }
      }
    } else {
      const target = perSetTarget(p, index);
      const reps = set.reps;
      if (target !== null && reps !== null && reps < target) {
        return { kind: "missed", why: `set ${index + 1} made ${reps} of ${target} reps` };
      }
    }
    const loadTarget = perSetLoadTarget(p, index);
    const lifted = set.weightKg;
    if (loadTarget !== null && loadTarget > 0 && lifted !== null && lifted + 1e-9 < loadTarget) {
      return {
        kind: "missed",
        why: `set ${index + 1} lifted ${kgText(lifted)} of ${kgText(loadTarget)} kg`,
      };
    }
    const rir = rirOf(set);
    if (minimumRIR !== null && rir !== null && rir < minimumRIR) {
      /* ⚠️ `String(format: "%.0f")` ROUNDS HALF-TO-EVEN: 2.5 → "2", 3.5 → "4". `toFixed(0)`
         rounds half away from zero: 2.5 → "3". RIR is `10 - rpe` and every RPE this app logs is
         a whole or half number, so the halves are REACHABLE — this is the `%.f` divergence from
         the codex, landing in a sentence a coach reads. Transcribed explicitly. */
      return { kind: "missed", why: `set ${index + 1} finished at ${halfToEven(rir)} reps in reserve` };
    }
  }
  return { kind: "clean" };
}

/** `String(format: "%.0f", x)` — round half to EVEN, which is what C does and what `toFixed(0)`
 *  does not. */
export function halfToEven(value: number): string {
  const floor = Math.floor(value);
  const diff = value - floor;
  let n: number;
  if (diff > 0.5) n = floor + 1;
  else if (diff < 0.5) n = floor;
  else n = floor % 2 === 0 ? floor : floor + 1;
  return String(n);
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * SLICE 2 — THE ORCHESTRATOR, WHICH REFUSES EVERYTHING IT CANNOT YET DO, LOUDLY.
 *
 * 🔴 THE ONE DECISION THAT SHAPES THIS WHOLE FILE. The Swift has an `unhandledRule` branch for a
 * rule string *this build does not implement*: it leaves the prescription unchanged and says so.
 * **A PORT GAP MUST NOT REUSE THAT ANSWER.** "This build has no such rule" and "this port has not
 * reached a rule the Swift implements" look identical on screen and are opposites underneath: the
 * first is correct behaviour, the second is a rule that SHOULD have moved a load and did not.
 * Reusing `unhandledRule` would hand a coach a week that quietly stopped progressing, with a
 * sentence explaining why it was fine.
 *
 * So `nextWeek` returns a REFUSAL for the whole call — never a per-row shrug — the moment its
 * input reaches anything `SLICE_STATE` has not claimed. The refusal names every gap it found, so
 * one call tells you what the next slice must be rather than one thing at a time.
 * ══════════════════════════════════════════════════════════════════════════════════════════════ */

/** A prescription as the rules mutate it. Wider than `RulePrescription` because the orchestrator
 *  writes back more than `performance` reads. */
export interface RuleRowPrescription extends RulePrescription {
  repsMax: number | null;
  holdSecondsMax: number | null;
  perSetSeconds: number[];
  perSetRestSeconds: number[];
  perSetTypes: string[];
  /** `assist_kg` — the band/counterweight kilos an assisted row carries. ⚠️ A ROW MAY NOT CARRY
   *  ASSISTANCE AND LOAD AT ONCE: the database forbids the pair, so §5.3's ladder switch REFUSES
   *  loudly on an assisted row rather than writing a prescription that cannot be stored. */
  assistKg: number | null;
  progressionConfig: Record<string, Json> | null;
  progressionRule: string;
  selectionReason: string;

  /* ── THE FIVE FIELDS §5.5 READS, ADDED AHEAD OF THE PASS AND INERT UNTIL IT LANDS ──────────
     Widened in their own commit, on purpose: a type change and a behaviour change landing
     together leave two suspects for anything that moves. Every one of them is a REAL field of
     `ProgramSessionExercise` that this twin had simply not needed yet, cited to the Swift line
     that reads it, and NOT one of them is consulted by any rule in this file today. The proof of
     that is the whole existing suite still passing unchanged, plus `v6rulesRowIdentity.test.ts`,
     which goes on asserting it after tonight. */

  /** `ProgramSessionExercise.id`. ⚠️ CARRIED FOR FIDELITY, NOT FOR IDENTITY: the Swift's own
   *  `PlannedRow` comment (V6Progression.swift:483) says this struct describes the SHAPE of next
   *  week's row and that the CALLER mints new ids when it writes. §5.5 sets a fresh one on the row
   *  it introduces (:1519), which is the only place this file will ever write it. */
  id: string;
  /** The LIBRARY row this prescribes. §5.5 reads it to find the SIBLING a converted slot moves
   *  into, V6Progression.swift:1405. */
  exerciseId: string;
  /** ⚠️ THE SIBLING MUST BE IN THE SAME SESSION, V6Progression.swift:1406 — the same exercise on
   *  another day is a different row, and converting a slot into it would move work between days. */
  programSessionId: string;
  /** The superset/circuit group this row belongs to, or null for straight sets. §5.5's paired-row
   *  refusal turns on it, V6Progression.swift:1489. */
  formatGroupId: string | null;
  /** Cleared on an introduced row, V6Progression.swift:1531 — a new rung inherits none of the
   *  parent's prose. */
  notes: string;
}

export interface ExerciseFacts {
  id: string;
  name: string;
  isStraightArm: boolean;
  loadsGrip: boolean | null;
  loadsPronationSupination: boolean | null;
  painRegions: string[];
}

export interface RuleRow {
  id: string;
  prescription: RuleRowPrescription;
  facts: ExerciseFacts;
  /** ⚠️ NULLABLE, AND THE NULL IS REPORTED. A row belonging to no section is one neither the
   *  deload nor the straight-arm budget can see the role of. */
  sectionRole: "warm_up" | "main" | "cool_down" | "recovery" | null;
}

export interface RuleInput {
  weekNumber: number;
  isDeload: boolean;
  rows: RuleRow[];
  logs: Record<string, { sets: RuleLoggedSet[] }>;
  config: ProgramConfig;
  /** The regions the athlete reported pain in this week. §5.12's freeze reads it, and
   *  `wristPainReported` is DERIVED from it — see below. */
  painfulRegions: string[];
  gateAnswers: Record<string, boolean>;
  testedMaxSeconds: Record<string, number>;
  /** §5.5 — facts about exercises this week's rows do not contain yet, so a promotion gate can
   *  introduce one. Keyed by EXERCISE id. Mirrors `Input.catalogue`, V6Progression.swift:374, and
   *  its rule travels with it: **a gate whose target is missing from here is REFUSED with a reason
   *  rather than introduced as an unnamed row.** */
  catalogue: Record<string, ExerciseFacts>;
  /** Mints the id for a row §5.5 INTRODUCES. Mirrors `introduced.id = UUID()`,
   *  V6Progression.swift:1519.
   *
   *  🔴 INJECTED, WITH NO DEFAULT IN THIS FILE, AND THE REASON IS THE PARITY CHECK RATHER THAN
   *  PURITY. The harness emitter pins ITS id minter against a committed literal so the fixture is
   *  byte-stable, and the morning's parity check replays that fixture through this twin —
   *  DETERMINISTIC IDS ARE WHAT MAKE AN INTRODUCED ROW COMPARABLE AT ALL. A `crypto.randomUUID()`
   *  baked in here would make every introduced row differ from the fixture on every run, and the
   *  one pass that CREATES rows would be the one pass parity could never check. The Swift mints
   *  inline because it has no fixture to match. The Deno endpoint passes `crypto.randomUUID`; the
   *  tests pass a counter. */
  mint: () => string;
}

/** Mirrors `Input.wristRegion`, V6Progression.swift:401 — `CanonicalMuscle.Group.arms.rawValue`. */
export const WRIST_REGION = "arms";

/** Mirrors `Input.wristPainReported`, V6Progression.swift:402.
 *
 *  🔴 DERIVED, AND IT USED TO BE A STORED BOOLEAN ON `RuleInput` — WHICH WAS A DIVERGENCE THIS
 *  PORT INTRODUCED, NOT A CHOICE. The Swift has no such field: wrist pain IS "the athlete reported
 *  pain in the arms region", and storing it separately made two stores of one fact where only one
 *  can be right. A caller could hand this twin `painfulRegions: []` with `wristPainReported: true`
 *  and get a wrist ramp for an athlete who reported nothing, or the reverse — and nothing would
 *  have said so, because each field was internally consistent. Found while porting §5.8, which is
 *  the first pass that reads it. */
export function wristPainReported(input: { painfulRegions: string[] }): boolean {
  return input.painfulRegions.includes(WRIST_REGION);
}

/** Mirrors `V6Progression.Stop`, V6Progression.swift:452.
 *
 *  🔴 A PROPOSAL, NEVER AN ACTION, and the Swift says so in its own words: §5.12's three stops all
 *  end in "propose" or "surface an alert". The module does not cancel an athlete's block or insert
 *  an unscheduled week — it says what it thinks and the review screen says it out loud. A twin
 *  that acted on one of these would be taking a decision nobody asked it to take. */
export interface Stop {
  /** `lightWeekProposed` — two consecutive LOGGED weeks in which a row failed its prescription.
   *  `cycleTerminationProposed` — four logged weeks in which NOTHING anywhere progressed.
   *  `painAlert` — pain reported in a region this program trains. */
  kind: "lightWeekProposed" | "cycleTerminationProposed" | "painAlert";
  message: string;
}

/** Mirrors `V6Progression.BudgetVerdict`, V6Progression.swift:507.
 *  ⚠️ `notConfigured` IS ITS OWN STATE, not a silent `withinBudget`: a program with no budget has
 *  not passed a check, it has not had one. */
export interface BudgetVerdict {
  state: "withinBudget" | "refusedAnAddition" | "overBudget" | "notConfigured";
  allowanceSeconds: number | null;
  prescribedSeconds: number;
  /** Rows whose straight-arm set addition was taken back to stay inside the allowance. */
  refusedRowIds: string[];
}

export interface Change { rowId: string; exerciseName: string; kind: ChangeKind; reason: string }
export interface PlannedRow {
  /** LAST week's row id. ⚠️ For a row §5.5 INTRODUCED this is the PARENT's id — the row that
   *  earned the promotion — because that is the only identity the new row has yet.
   *  Mirrors `V6Progression.PlannedRow.sourceRowId`, V6Progression.swift:486. */
  sourceRowId: string;
  prescription: RuleRowPrescription;
  /** Non-nil ONLY for a row §5.5 introduced. Mirrors V6Progression.swift:492, and its comment is
   *  the reason it is not decoration: `sourceRowId` points at the PARENT, so every consumer that
   *  looks facts up by that id — the §4 straight-arm budget above all — would otherwise price the
   *  new rung as though it were the old one. A straddle planche introduced under a tuck planche is
   *  a different exercise with its own name and its own answer to `is_straight_arm`.
   *
   *  ⚠️ NULL, NOT OPTIONAL, AND EVERY ROW STATES IT. "This row was introduced and here are its
   *  facts" and "this row was not introduced" are two answers, and an absent key is a third thing
   *  that reads as either. */
  introducedFacts: ExerciseFacts | null;
}

export type NextWeekResult =
  | {
      ok: true; rows: PlannedRow[]; changes: Change[]; warnings: string[];
      /** What a rule needs the ATHLETE to answer before it can decide (§5.10). ⚠️ A question is
       *  a first-class output, not a warning: a warning is something that went wrong, and this is
       *  the engine correctly declining to judge something a log cannot see. */
      questions: string[];
      /** §5.13 — what the week just finished MEASURED, keyed by EXERCISE, for the caller to write
       *  into `profiles.skill_max_holds`. Mirrors `NextWeekPlan.testedMaxUpdates`,
       *  V6Progression.swift:530.
       *
       *  ⚠️ A FIRST-CLASS OUTPUT RATHER THAN A SIDE EFFECT, and empty when nothing was measured —
       *  which is not the same as "the athlete has no max". A caller that treats an empty map as a
       *  set of zeroes would re-dose a whole program off a measurement that never happened. */
      testedMaxUpdates: Record<string, number>;
      /** §5.12's proposals. Mirrors `Output.stops`, V6Progression.swift:527. Empty on a deload,
       *  by the pass's own first guard. */
      stops: Stop[];
      /** §4's verdict. Mirrors `Output.budget`, V6Progression.swift:519. */
      budget: BudgetVerdict;
    }
  /** ⚠️ THE WHOLE CALL, NEVER A ROW. See the block comment above. */
  | { ok: false; unported: string[]; message: string };

/** Mirrors `V6Progression.resize`, V6Progression.swift:2117 — an EMPTY array stays empty (a
 *  column is complete or empty, never partial), a longer one is truncated, a shorter one is
 *  padded with its own LAST value. */
export function resize<T>(values: T[], count: number): T[] {
  if (values.length === 0) return [];
  if (values.length === count) return values;
  if (values.length > count) return values.slice(0, count);
  const last = values[values.length - 1] as T;
  return [...values, ...Array.from({ length: count - values.length }, () => last)];
}

/** Mirrors `V6Progression.setSets`, V6Progression.swift:2093.
 *
 *  ⚠️ IT DELIBERATELY DOES **NOT** RESYNC THE REP RANGE, and the Swift's comment is the reason:
 *  this is a shared mutator every rule reaches, and `perSetReps` means two different things
 *  depending on who owns the row — `rep_ladder`'s IS the range, `accessory_double`'s is the
 *  CURRENT uniform target while `repsMin`/`repsMax` stays the fixed policy range the card shows.
 *  A call here could not tell them apart, and did not: it was overwriting one rule's range with
 *  whichever number the other had written. */
export function setSets(p: RuleRowPrescription, sets: number): void {
  const n = Math.max(1, sets);
  p.sets = n;
  p.perSetReps = resize(p.perSetReps, n);
  p.perSetWeightKg = resize(p.perSetWeightKg, n);
  p.perSetSeconds = resize(p.perSetSeconds, n);
  p.perSetRestSeconds = resize(p.perSetRestSeconds, n);
  if (p.perSetTypes.length > n) p.perSetTypes = p.perSetTypes.slice(0, n);
}

/** Mirrors `V6Progression.applyDeload`, V6Progression.swift:819.
 *  §5.6 — sets × 0.5, floored, minimum 1, **MAIN ONLY**, and **LOADS AND HOLDS UNCHANGED**: a v6
 *  deload is the same training, less of it. What it took away is stored on the row so the restore
 *  is a fact rather than an inference. */
export function applyDeload(p: RuleRowPrescription, role: RuleRow["sectionRole"], config: RowConfig): void {
  if (role !== "main") return;
  const before = p.sets;
  const halved = Math.max(1, Math.floor(before * 0.5));
  if (halved === before) return;
  const restore: Record<string, Json> = { sets: before };
  if (p.perSetReps.length > 0) restore["per_set_reps"] = [...p.perSetReps];
  config.set("pre_deload", restore);
  setSets(p, halved);
}

/** Mirrors `V6Progression.restoreAfterDeload`, V6Progression.swift:838.
 *  ⚠️ IT RUNS BEFORE THIS WEEK'S PROGRESSION IS APPLIED, or every wave would halve the program.
 *  ⚠️ AND THE LADDER IS ONLY RESTORED WHEN IT STILL FITS (`reps.count == sets`) — a stored array
 *  of the wrong length is a partial column, which V7-1 says to drop rather than pad. */
export function restoreAfterDeload(p: RuleRowPrescription, config: RowConfig): number | null {
  const stored = config.raw["pre_deload"];
  if (stored === null || stored === undefined || typeof stored !== "object" || Array.isArray(stored)) {
    return null;
  }
  const o = stored as Record<string, Json>;
  const setsRaw = o["sets"];
  if (typeof setsRaw !== "number" || !Number.isFinite(setsRaw)) return null;
  const sets = Math.trunc(setsRaw);
  setSets(p, sets);
  const repsRaw = o["per_set_reps"];
  if (Array.isArray(repsRaw)) {
    const reps = repsRaw
      .filter((v): v is number => typeof v === "number" && Number.isFinite(v))
      .map(Math.trunc);
    if (reps.length === sets) {
      p.perSetReps = reps;
      /* ⚠️ `syncRepRange` IS OWED HERE FOR `rep_ladder` AND IS PART OF SLICE 5, not this one.
         The gate below refuses any input carrying a rule, so this branch is unreachable today —
         said out loud because an unreachable branch that looks finished is how a port loses a
         line without noticing. */
    }
  }
  config.set("pre_deload", undefined);
  return sets;
}

/** Everything this port has NOT reached, checked against the input BEFORE anything is built.
 *
 *  ⚠️ CHECKED UP FRONT AND REPORTED AS A LIST, not discovered row by row. One call then tells you
 *  what the next slice must be, instead of one thing at a time across eight runs. */
export function portGaps(input: RuleInput): string[] {
  const gaps = new Set<string>();
  /** ⚠️ EVERY FEATURE CLAUSE ASKS `SLICE_STATE.portedFeatures`, so the decision lives in ONE list
   *  rather than in the presence or absence of a line here. A clause deleted by hand when a pass
   *  landed is a clause nobody can audit afterwards; a clause that consults the list is one a
   *  reader can check against the code in a single glance. */
  const unlessPorted = (feature: string, label: string) => {
    if (!SLICE_STATE.portedFeatures.includes(feature)) gaps.add(label);
  };
  for (const row of input.rows) {
    const config = RowConfig.from(row.prescription.progressionConfig as Json | undefined);
    if (config === null) continue;
    const rule = config.rule;
    if (rule !== null && !(SLICE_STATE.portedRules as readonly string[]).includes(rule)) {
      gaps.add(`rule:${rule}`);
    }
    if (config.bool("test_max") === true) {
      unlessPorted("test_max", "feature:test_max (§5.13 retest)");
    }
    /* ⚠️ `gate` (the §5.5 PROMOTION gate on a row's config) is still unported and still refuses.
       `gateAnswers` itself is no longer a gap: §5.10's box ladder is ported and reads them, and the
       §5.5 consumer is refused by THIS clause instead. Gating on the ANSWERS would now refuse a
       box-ladder week for carrying exactly the answer it asked for last week. */
    if (config.raw["gate"] !== undefined) {
      unlessPorted("gate", "feature:gate (§5.5 promotion gates)");
    }
    if (config.int("target_sets") !== null) {
      unlessPorted("target_sets", "feature:target_sets (the introduction ramp)");
    }
  }
  if (wristPainReported(input)) {
    unlessPorted("wristPainReported", "feature:wristPainReported (§5.8 the wrist ramp)");
  }
  if (input.painfulRegions.length > 0) {
    unlessPorted("painfulRegions", "feature:painfulRegions (§5.12 the region freeze)");
  }
  if (input.config.budget !== null) {
    unlessPorted("straight_arm_budget", "feature:straight_arm_budget (§4)");
  }
  /* ⚠️ STILL REFUSED AFTER §5.13, AND THE REASON IS THE WHOLE POINT OF THE ENTRY ABOVE. This map
     is `doseBandNote`'s input, not `redose`'s: `redose` reads what THIS week measured
     (`testedMaxima`), while `doseBandNote` reports a dose that sits outside the 60–75% band using
     the maxima the CALLER already held. That function is not ported, so a caller passing the map
     would have it silently ignored — which is exactly the failure this gate exists to prevent. */
  if (Object.keys(input.testedMaxSeconds).length > 0) {
    unlessPorted("testedMaxSeconds", "feature:testedMaxSeconds (§5.13 doseBandNote)");
  }
  return [...gaps].sort();
}

/** Mirrors the shape of `V6Progression.nextWeek`, V6Progression.swift:646 — the part of it this
 *  port has reached.
 *
 *  WHAT IT DOES TODAY: the deload halve and its restore, the `unhandledRule` report for a rule
 *  string the SWIFT does not implement either, the config write-back, and the "belongs to no
 *  section" warning. Every row with a real rule, and every input touching a feature below, is
 *  REFUSED for the whole call.
 *
 *  ⚠️ THE `isDeload` DISAGREEMENT IS REPORTED, NEVER RESOLVED. `engine_config` and the program
 *  phase can disagree about a week; the PHASE is what the athlete sees, so the phase decides and
 *  the config is reported as wrong. A twin that silently preferred either one would make the two
 *  implementations disagree about a whole week of somebody's training. */
export function nextWeek(input: RuleInput): NextWeekResult {
  const gaps = portGaps(input);
  if (gaps.length > 0) {
    return {
      ok: false,
      unported: gaps,
      message:
        "The progression rules are only partly ported to TypeScript. This week needs " +
        `${gaps.join(", ")}, which has no twin yet, so NOTHING was decided — the Swift engine ` +
        "(V6Progression) is still the only implementation that may write a week.",
    };
  }

  const changes: Change[] = [];
  const warnings: string[] = [];
  const questions: string[] = [];
  const planned: PlannedRow[] = [];
  const straightArmAdditions: string[] = [];

  /* ── §5.13 ── WHAT THE WEEK JUST FINISHED MEASURED, derived before anything is built.
     Mirrors V6Progression.swift:664.

     ⚠️ DERIVED HERE RATHER THAN PASSED IN, and the Swift's comment is the reason: a
     caller-computed map would be a SECOND place that decides what a test is. A row is a retest
     when its own config says `test_max: true`, and the measurement is the best hold it logged.

     ⚠️ AND IT IS COMPUTED ON A DELOAD WEEK TOO. The re-dose below is inside the non-deload
     branch — a deload does not move a dose — but a max the athlete actually hit during a deload
     week is still a measurement, and dropping it would lose a real number for no reason. */
  const measured = testedMaxima(input.rows, input.logs);

  /* Every row's verdict, kept for the POST-PASSES. Mirrors V6Progression.swift:697.
     ⚠️ ASKED WITHOUT A MINIMUM RIR, and the Swift's comment is the reason: "was every set
     completed as prescribed" and "was it easy enough to add weight" are different questions, and
     only the FIRST is what §5.5's gates and §5.12's stops are counting. Passing the RIR bar here
     would make a clean-but-hard week look like a missed one to a gate. */
  /* ── §5.8 ── THE WEEK-LEVEL WRIST SIGNAL, COMPUTED ONCE. Mirrors V6Progression.swift:671.
     ⚠️ GRIP WORK BEING **LOGGED** IS HALF OF IT: "two consecutive pain-free weeks logged on the
     hang" is not satisfied by two weeks nobody trained. That is the three-state finding (clean /
     missed / notLogged) applied to a REHAB GATE rather than to a load — an athlete who stopped
     training entirely must not accumulate pain-free weeks towards unblocking rotation work. */
  const gripLogged = input.rows.some((row) =>
    row.facts.loadsGrip === true && (input.logs[row.id]?.sets.length ?? 0) > 0);
  const wristPainFreeWeek = !wristPainReported(input) && gripLogged;

  const verdicts: Record<string, Performance> = {};
  for (const row of input.rows) {
    verdicts[row.id] = performance(row.prescription, input.logs[row.id] ?? null);
  }

  const declared = input.config.declaresDeload(input.weekNumber);
  if (declared !== null && declared !== input.isDeload) {
    warnings.push(
      `engine_config says week ${input.weekNumber} is ` +
      `${declared ? "a deload" : "a build week"} and the program phase says ` +
      `${input.isDeload ? "a deload" : "a build week"}. The phase is what the athlete sees, so ` +
      "the phase decided this week — fix the config.",
    );
  }

  for (const row of input.rows) {
    const next: RuleRowPrescription = {
      ...row.prescription,
      perSetReps: [...row.prescription.perSetReps],
      perSetWeightKg: [...row.prescription.perSetWeightKg],
      perSetSeconds: [...row.prescription.perSetSeconds],
      perSetRestSeconds: [...row.prescription.perSetRestSeconds],
      perSetTypes: [...row.prescription.perSetTypes],
    };
    /* A row with no config is a warm-up, a cool-down or a mobility drill: it repeats unchanged,
       which is what an empty `progression_config` means and needs no branch on the far side. */
    const config = RowConfig.from(row.prescription.progressionConfig as Json | undefined)
      ?? RowConfig.fromRaw({});
    const name = row.facts.name;
    const rowChanges: Change[] = [];
    const record = (kind: ChangeKind, reason: string) =>
      rowChanges.push({ rowId: row.id, exerciseName: name, kind, reason });

    if (input.isDeload) {
      applyDeload(next, row.sectionRole, config);
      if (next.sets !== row.prescription.sets) {
        record("setsHalved",
          `${name}: ${row.prescription.sets} → ${next.sets} sets for the deload. ` +
          "Load and hold seconds are unchanged.");
      }
    } else {
      const restored = restoreAfterDeload(next, config);
      if (restored !== null) {
        record("setsRestored", `${name}: back to ${restored} sets now the deload week is done.`);
      }
      /* ── §5.13 ── A RETEST ENDS THE RUNG, AND IT IS THE ONLY THING THAT DOES.
         Mirrors V6Progression.swift:719.

         §5.4's freeze — hold seconds never lengthen — is a rule about staying INSIDE a rung, not
         a rule that seconds are immutable forever. A measured max is what ends one, which is why
         the re-derivation lives here and nowhere else, and why it runs BEFORE the progression
         rule: this week's rung is the new one, and a rule asked about the old one would decide
         against a dose that no longer exists.

         ⚠️ KEYED ON `row.facts.id`, THE EXERCISE — never on the row. The max test of the planche
         and the working planche row are two ROWS naming one MOVEMENT, and it is the movement the
         measurement belongs to. Keyed by row, the test would re-dose only itself and the working
         row it exists to serve would never move. */
      const maximum = measured[row.facts.id];
      if (maximum !== undefined) redose(next, maximum, record, name);

      /* ── §5.8 ── THE WRIST RAMP, BEFORE THE ROW'S OWN RULE. Mirrors V6Progression.swift:731.
         ⚠️ IT IS THE MORE SPECIFIC OF THE TWO SUSPENSIONS, so it decides first for the rows it
         OWNS; a row it does not own falls to §5.12's general region freeze (not ported yet, and
         still refused by name in `portGaps`). Two rules deciding one set count is how a program
         ends up fighting itself. */
      const rehab = applyWristRehab(row, next, config, wristPainReported(input),
                                    wristPainFreeWeek, record);
      if (rehab.warning !== null) warnings.push(rehab.warning);

      /* ── §5.12 ── THE GENERAL REGION FREEZE, the SECOND of the two suspensions.
         Mirrors V6Progression.swift:741. ⚠️ IT ONLY REACHES ROWS §5.8 DOES NOT OWN: the wrist ramp
         is the more specific rule and has already decided for its own rows, and two rules
         deciding one set count is how a program ends up fighting itself.
         ⚠️ AND THE SENTENCE SAYS THE BUILD READS PAIN BY **REGION**, not per exercise — so the
         athlete is told why a row they did not complain about is also on hold. */
      const painFrozen = !rehab.ownsThisRow
        && row.facts.painRegions.some((r) => input.painfulRegions.includes(r));
      if (painFrozen) {
        const regions = row.facts.painRegions
          .filter((r) => input.painfulRegions.includes(r))
          .sort().join(", ");
        record("painFreeze",
          `${name}: progression is paused — you reported pain in ${regions}, and this exercise `
          + "trains it. The prescription repeats. (This build reads pain by REGION, not per "
          + `exercise, so it pauses everything that trains ${regions}.)`);
      }

      const rule = config.rule;
      if (config.bool("test_max") === true) {
        /* A test is a MEASUREMENT, NOT TRAINING: it is not progressed, and (once §5.12 lands) it
           does not accrue the stop counters either way. Mirrors V6Progression.swift:752.
           ⚠️ It is still re-dosed by the clause above — the freeze is about the RULE, not about the
           re-derivation — so a retest row whose own dose is now wrong gets corrected like any
           other row naming that exercise. */
        record("frozen", `${name}: a retest, so nothing is progressed from it.`);
      } else if (rehab.suspendsProgression || painFrozen) {
        /* The reason has already been recorded by the suspension itself — `rehabFlare`,
           `rehabRamp` or `rehabBlocked` each say what they did and why. Recording a second,
           vaguer line here would tell the athlete the same thing twice in different words. */
      } else if (rule !== null) {
        applyRule(rule, row, next, config, input.logs[row.id] ?? null, record,
                  input.config.emphasis(input.weekNumber),
                  /* ⚠️ `in` RATHER THAN A TRUTHINESS TEST: `false` is an ANSWER ("the range was not
                     full") and `undefined` is "not answered yet", and they must not collapse. */
                  row.id in input.gateAnswers ? input.gateAnswers[row.id] : undefined,
                  (text) => questions.push(text));
      } else if (config.ruleName !== "") {
        record("unhandledRule",
          `${name}: the progression rule "${config.ruleName}" is not implemented in this build, ` +
          "so the prescription is unchanged.");
      }

      /* ── §5.7 ── A ROW THAT ENTERED BELOW ITS TARGET DOSE CLIMBS ONE SET A WEEK.
         Mirrors V6Progression.swift:775.

         ⚠️ SKIPPED WHEN THIS WEEK ALREADY BOUGHT A SET, so nothing gains two at once — §5.4's
         emphasis clause and this ramp are two different reasons to add a set and an athlete who
         earned one should not also be handed the other.
         ⚠️ AND SKIPPED UNDER EITHER SUSPENSION. The wrist ramp is ALSO deciding this row's set
         count, and a rule that cannot see it would fight it one set a week; the region freeze
         means the row is meant to repeat. */
      const rampTarget = config.int("target_sets");
      if (!rowChanges.some((c) => c.kind === "setAdded")
          && !rehab.ownsThisRow && !painFrozen
          && rampTarget !== null && next.sets < rampTarget) {
        const before = next.sets;
        setSets(next, before + 1);
        record("setAdded",
          `${name}: ${before} → ${next.sets} sets — still ramping up to its full ${rampTarget} `
          + "after being introduced.");
      }

      /* ── §5.12 ── THE COUNTERS THE STOPS ARE READ FROM, updated LAST so they see what the
         week actually DID. Mirrors V6Progression.swift:783.
         ⚠️ INSIDE THE NON-DELOAD BRANCH: a deload week updates NEITHER counter, because its rows
         are halved by design and "no progress" there is the plan working. A build that counted it
         would meet an athlete coming off a deload with a proposal to abandon their block. */
      updateStopCounters(config, verdicts[row.id] ?? { kind: "notLogged" }, rowChanges);
    }

    if (Object.keys(config.raw).length > 0) {
      next.progressionConfig = config.json;
      /* ✅ THE HALF SLICE 2 LEFT NAMED AND UNBUILT, LANDED. Its note said `prose` "is NOT ported —
         it belongs with the rules whose sentences it writes", and all eight of those rules now
         exist, so the sentence is regenerated here exactly as the Swift does it
         (V6Progression.swift:797).
         ⚠️ ONLY WHEN IT IS NON-EMPTY, and that guard is the Swift's. An empty regeneration means
         the row carries no rule this build recognises, and blanking `progressionRule` would DELETE
         a sentence a coach typed — the card would go silent about a row that is still prescribed. */
      const regenerated = prose(config, name);
      if (regenerated !== "") next.progressionRule = regenerated;
    }
    const firstReal = rowChanges.find((c) => c.kind !== "frozen");
    if (firstReal !== undefined) next.selectionReason = firstReal.reason;

    if (row.sectionRole === null) {
      warnings.push(
        `${name} belongs to no session section, so neither the deload nor the straight-arm ` +
        "budget can see what role it plays.");
    }

    /* §5.4's dosing band, as a CHECK. Mirrors V6Progression.swift:808. */
    const bandNote = doseBandNote(row, next, input.testedMaxSeconds);
    if (bandNote !== null) warnings.push(bandNote);

    /* ⚠️ `null`, STATED. §5.5 is the only pass that ever sets this, and a row it did not
       introduce says so rather than leaving the key absent. */
    /* ── §4 ── WHICH ADDITIONS THIS WEEK GRANTED, for the budget to take back if it must.
       Mirrors V6Progression.swift:812. ⚠️ ONLY ADDITIONS MADE **THIS** WEEK are candidates: an
       overrun the week INHERITED cannot be fixed by taking back something nobody granted, which
       is why the verdict reports it instead (D-V6.7, "refuse, then flag"). */
    if (rowChanges.some((c) => c.kind === "setAdded")
        && isBudgeted(row.facts, row.sectionRole, next)) {
      straightArmAdditions.push(row.id);
    }
    planned.push({ sourceRowId: row.id, prescription: next, introducedFacts: null });
    changes.push(...rowChanges);
  }

  /* ── §5.5 ── THE PROMOTION GATES, AFTER THE ROW LOOP AND BEFORE §4's BUDGET.
     Mirrors V6Progression.swift:820. A POST-PASS because a conversion moves a set from a PARENT
     row to its SIBLING, so it needs the whole week in front of it: done mid-loop it would mutate a
     row the loop has already finished with, or one it has not reached — the two shapes of one bug.
     ⚠️ §4's budget is not ported yet. When it lands it goes AFTER this, because a promotion moves
     straight-arm seconds around and an allowance enforced first would be enforced against a week
     that no longer exists (the Swift's own ordering note, :822). */
  const gated = applyPromotionGates(input, planned, changes, verdicts, questions, warnings);

  /* ── §4 ── THE STRAIGHT-ARM BUDGET, ENFORCED AT BUILD TIME (D-V6.7) AND **LAST**.
     Mirrors V6Progression.swift:832. The ordering is the Swift's own and it is not arbitrary: a
     promotion moves straight-arm seconds around — the parent loses a set, the new rung gains one,
     and the two can prescribe different hold lengths — so an allowance enforced before §5.5 would
     be enforced against a week that no longer exists. */
  const budgeted = enforceBudget(input, gated.planned, gated.changes, straightArmAdditions);

  return {
    ok: true, rows: budgeted.planned, changes: budgeted.changes, warnings, questions,
    testedMaxUpdates: measured, budget: budgeted.verdict,
    /* §5.12's proposals, derived from the FINAL rows — after §5.5 has introduced and converted
       AND after §4 has taken back what it must, because a row that arrived or shrank this week is
       part of the week the stops describe. */
    stops: stops(budgeted.planned, input),
  };
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * SLICE 3 — `marker` (§3) and `barbell_linear` (§5.1).
 *
 * Two families in one commit because `marker` is THREE LINES — "logged, never auto-progressed,
 * nothing to do and nothing to say" — and a commit that ports three lines and calls itself a slice
 * tells you less than one that ports the trivial family beside the first real one and says so.
 * `barbell_linear` is the family that proves the dispatch, the derived state and the sentence.
 * ══════════════════════════════════════════════════════════════════════════════════════════════ */

/** Mirrors `V6Progression.apply(rule:…)`, V6Progression.swift:845 — the dispatch.
 *  ⚠️ EVERY ARM IS EITHER IMPLEMENTED OR ABSENT FROM `SLICE_STATE.portedRules`, and `portGaps`
 *  refuses the call for the absent ones BEFORE this is reached. There is deliberately no `default`
 *  that shrugs: an arm added here without its `portedRules` entry is unreachable, and an entry
 *  added without its arm is a TypeScript error. The two cannot drift. */
export function applyRule(
  rule: Rule,
  row: RuleRow,
  next: RuleRowPrescription,
  config: RowConfig,
  log: { sets: RuleLoggedSet[] } | null,
  record: (kind: ChangeKind, reason: string) => void,
  /** The skill family this week's wave is built around. ⚠️ NULL MATCHES NOTHING rather than
   *  everything — §5.4's "+1 set" dimension is unlocked by a NAMED emphasis and by nothing else. */
  emphasis: string | null = null,
  /** §5.10's armed question. ⚠️ THREE-STATE ON PURPOSE: true, false, and **undefined meaning the
   *  athlete has not answered yet** — which is neither a yes nor a no and must keep waiting. */
  gateAnswer: boolean | undefined = undefined,
  /** Where a rule puts a question for the athlete. Nothing else may ask one. */
  question: (text: string) => void = () => {},
): void {
  const name = row.facts.name;
  switch (rule) {
    case "marker":
      // §3 — logged, never auto-progressed. Nothing to do, and nothing to say.
      return;

    case "barbell_linear":
      return barbellLinear(name, row.prescription, next, config, log, record);

    case "weighted_bw_biweekly":
      return weightedBwBiweekly(name, row.prescription, next, config, log, record);

    case "rep_ladder":
      return repLadder(name, row.prescription, next, config, log, record);

    case "hold_rung":
      return holdRung(name, row.prescription, next, config, log, record, emphasis);

    case "distance_cm":
      return distanceCm(name, row.prescription, next, config, log, record);

    case "box_height_cm":
      return boxHeightCm(name, row.prescription, config, log, record, gateAnswer, question);

    case "accessory_double":
      return accessoryDouble(name, row.prescription, next, config, log, record);
  }
  /* 🔴 NO `default`, AND NO UNPORTED ARM LEFT TO THROW FROM. Every family now has a case, so the
     switch is EXHAUSTIVE and TypeScript enforces it: adding a ninth rule to the `Rule` union
     without an arm here is a compile error, which is the strongest form this guard can take. The
     `throw` that stood here while families were missing has done its job and is gone rather than
     left as a branch that can never run — a dead guard is one a reader has to reason about. */
}

/** §5.1 — barbell lower/hinge. Mirrors `V6Progression.swift:909`.
 *
 *  THE RULE AS A SENTENCE: add `step_kg` while every set is completed at or above the RIR bar;
 *  two consecutive missed jumps hold the load for `hold_weeks`.
 *
 *  ⚠️ A HOLD CONSUMES ITS WEEKS WHATEVER HAPPENS — "hold load 2 weeks, then resume" — so the
 *  countdown runs BEFORE the performance is even asked for. A twin that checked performance first
 *  would let a good week cut a hold short, which is the opposite of what a hold is for.
 *
 *  ⚠️ AND THE SENTENCE CLAIMS ONLY THE EVIDENCE IT HAS (the 2026-09-10 review, §3b). `min_rir`
 *  bites on a LOGGED RPE alone and silence passes, so "with at least 2 reps in reserve" over three
 *  sets carrying no RPE reported the pass as a MEASUREMENT — and a coach reads that as the
 *  athlete's own report. The reserve is asserted only when EVERY completed set carried an RPE;
 *  otherwise the sentence says what was actually logged.
 */
function barbellLinear(
  name: string,
  /* 🔴 THE PERFORMANCE IS JUDGED AGAINST WHAT WAS **PRESCRIBED**, NEVER AGAINST THE ROW BEING
     BUILT, AND THE FIRST DRAFT OF THIS PORT GOT IT WRONG. By the time a rule runs, `next` may
     already carry `restoreAfterDeload`'s restored set count — 4 where the athlete was asked for 2
     — and `performance` compares `done.count < p.sets`. Judging against `next` would read a week
     trained exactly as asked as "only 2 of 4 sets were completed", open §5.1's hold, and stall a
     lift for two weeks the first time it came out of a deload. The Swift passes `row`, and so does
     this. Caught by reading the Swift's argument rather than by a test — there is no fixture for a
     deload restore under a rule yet, because the restore's ladder branch is slice 5's. */
  prescribed: RulePrescription,
  next: RuleRowPrescription,
  config: RowConfig,
  log: { sets: RuleLoggedSet[] } | null,
  record: (kind: ChangeKind, reason: string) => void,
): void {
  /* ⚠️ THE DEFAULTS ARE THE SWIFT'S AND ARE NOT "SENSIBLE VALUES" — 2.5 kg, 2 weeks, 2 RIR are
     what §5.1 states. Changing one here would change what a row with a sparse config does, in one
     implementation only. */
  const step = config.double("step_kg") ?? 2.5;
  const holdWeeks = config.int("hold_weeks") ?? 2;
  const bar = config.double("min_rir") ?? 2;

  const remaining = config.int("hold_remaining");
  if (remaining !== null && remaining > 0) {
    config.set("hold_remaining", remaining - 1);
    const left = remaining - 1;
    record("loadHeld",
      `${name}: holding at ${kgText(next.targetWeightKg ?? 0)} kg — ` +
      `${left} week${left === 1 ? "" : "s"} of the hold left.`);
    return;
  }

  const verdict = performance(prescribed, log, bar);
  if (verdict.kind === "notLogged") {
    record("frozen", `${name}: nothing was logged, so the load stays where it is.`);
    return;
  }
  if (verdict.kind === "clean") {
    const before = next.targetWeightKg ?? 0;
    /* ⚠️ THE STORED LOAD IS SNAPPED TO THE EQUIPMENT GRID AND THE SENTENCE IS NOT — the Swift says
       `kg(before + step)`, the raw sum. They agree on every step this athlete's plates can make and
       they are DIFFERENT EXPRESSIONS, so a twin that "tidied" the sentence to read the snapped
       value would diverge the first time a config named a step off the 0.25 grid. Transcribed as
       written, deliberately. */
    next.targetWeightKg = snappedKg(before + step);
    config.set("consecutive_fails", 0);
    const completedSets = (log?.sets ?? []).filter((s) => s.completed);
    const withEffort = completedSets.filter((s) => s.rpe !== null).length;
    let effort: string;
    if (completedSets.length > 0 && withEffort === completedSets.length) {
      effort = `with at least ${kgText(bar)} reps in reserve`;
    } else if (withEffort === 0) {
      effort = "(no effort was logged)";
    } else {
      effort = `(effort was logged on ${withEffort} of ${completedSets.length} sets)`;
    }
    record("loadIncrease",
      `${name}: ${kgText(before)} → ${kgText(before + step)} kg — every set was completed ${effort}.`);
    return;
  }
  const fails = (config.int("consecutive_fails") ?? 0) + 1;
  if (fails >= 2) {
    config.set("consecutive_fails", 0);
    config.set("hold_remaining", holdWeeks);
    record("loadHeld",
      `${name}: two missed jumps in a row (${verdict.why}) — holding ` +
      `${kgText(next.targetWeightKg ?? 0)} kg for ${holdWeeks} weeks.`);
    return;
  }
  config.set("consecutive_fails", fails);
  record("loadHeld",
    `${name}: ${verdict.why}, so the load repeats at ${kgText(next.targetWeightKg ?? 0)} kg.`);
}

/** `AssistLoad.snapped` for a non-optional load. Kept here rather than imported from
 *  `progression.ts` only because that module's version takes a nullable — same grid, same
 *  rounding, ONE definition of each. */
function snappedKg(value: number): number {
  const step = 0.25;
  /* Was an open-coded second copy of `roundHalfAwayFromZero` until §5.13 needed the same rule for
     seconds. One definition now, imported at the top of this file, for the reason stated there. */
  return roundHalfAwayFromZero(value / step) * step;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * SLICE 4 — `weighted_bw_biweekly` (§5.2): weighted bodyweight, +step every `every_weeks` clean
 * weeks. Its own slice because it is the first rule whose decision depends on a COUNTER rather
 * than on this week alone, and a counter is the thing a twin gets subtly wrong.
 * ══════════════════════════════════════════════════════════════════════════════════════════════ */

/** §5.2 — mirrors `V6Progression.swift:959`.
 *
 *  ⚠️ THREE THINGS THAT LOOK LIKE DETAILS AND ARE NOT.
 *  **(1) THE RIR BAR IS NOT ASKED FOR HERE.** `performance` is called with no `minimumRIR`, unlike
 *  §5.1 — the spec states the bar for the barbell lift and not for this one, and adding it "for
 *  consistency" would stall a weighted pull-up on an RPE nobody was asked to give.
 *  **(2) A MISS RESETS THE COUNTER TO ZERO; A WEEK NOBODY LOGGED LEAVES IT ALONE.** Those are
 *  different answers to different questions, and collapsing them would let a holiday cost an
 *  athlete a step they had already earned — the same three-state finding (clean / missed /
 *  notLogged) that `cleanStreak` exists for.
 *  **(3) `every_weeks` IS FLOORED AT 1.** A config saying 0 would otherwise make `earned >= every`
 *  true on every clean week AND on the zeroth, which is a rule with no counter at all.
 */
function weightedBwBiweekly(
  name: string,
  prescribed: RulePrescription,
  next: RuleRowPrescription,
  config: RowConfig,
  log: { sets: RuleLoggedSet[] } | null,
  record: (kind: ChangeKind, reason: string) => void,
): void {
  const step = config.double("step_kg") ?? 2.5;
  const every = Math.max(1, config.int("every_weeks") ?? 2);
  const verdict = performance(prescribed, log);
  if (verdict.kind === "notLogged") {
    record("frozen", `${name}: nothing was logged, so the added load stays where it is.`);
    return;
  }
  if (verdict.kind === "missed") {
    config.set("clean_weeks", 0);
    record("loadHeld", `${name}: ${verdict.why}, so the added load repeats.`);
    return;
  }
  const earned = (config.int("clean_weeks") ?? 0) + 1;
  if (earned >= every) {
    const before = next.targetWeightKg ?? 0;
    next.targetWeightKg = snappedKg(before + step);
    config.set("clean_weeks", 0);
    /* Same split as §5.1: the COLUMN is snapped, the SENTENCE is the raw sum. */
    record("loadIncrease",
      `${name}: +${kgText(step)} kg to ${kgText(before + step)} kg — ${every} clean weeks in a row.`);
    return;
  }
  config.set("clean_weeks", earned);
  /* ⚠️ `frozen`, NOT `loadHeld`. A clean week on the way to a step is not the same event as a load
     held because the athlete missed, and the coach's screen groups by kind — the Swift chose
     `frozen` here and `loadHeld` in the miss arm, and a twin that "tidied" them to one kind would
     merge two different weeks under one heading. */
  record("frozen", `${name}: clean week ${earned} of ${every} before the next ${kgText(step)} kg.`);
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * SLICE 5 — `rep_ladder` (§5.3): rep-based skills. +1 rep to ONE set a week; at the target shape,
 * swap to bodyweight-plus-load and reset the ladder.
 *
 * Wider than the last two because it brings two primitives with it (`syncRepRange`, and `assistKg`
 * on the prescription) and because the SWITCH is a second decision inside one rule.
 * ══════════════════════════════════════════════════════════════════════════════════════════════ */

/** Mirrors `V6Progression.syncRepRange`, V6Progression.swift:2111.
 *  ⚠️ IT IS CALLED ONLY BY THE RULES WHOSE `perSetReps` **IS** THE RANGE — never by `setSets`, which
 *  every rule reaches; see the note there. An EMPTY ladder leaves the range alone rather than
 *  nulling it: a column is complete or empty, and an empty one says nothing about the range. */
export function syncRepRange(p: RuleRowPrescription): void {
  if (p.perSetReps.length === 0) return;
  p.repsMin = Math.min(...p.perSetReps);
  p.repsMax = Math.max(...p.perSetReps);
}

/** §5.3 — mirrors `V6Progression.swift:983`.
 *
 *  THE RULE AS A SENTENCE: on a clean week, add ONE rep to ONE set — the LAST set sitting at the
 *  ladder's lowest value, so `3,3,3,3,4` becomes `3,3,3,4,4` exactly as the spec writes it. When
 *  every set has reached `switch_reps`, swap to bodyweight-plus-load and start the ladder again.
 *
 *  ⚠️ BOTH NON-CLEAN VERDICTS ARE `frozen` HERE, unlike §5.2 where a miss is `loadHeld`. That is
 *  not an inconsistency to tidy: there is no LOAD on a rep ladder to hold, and calling it one
 *  would put a rep row under the coach's "load held" heading.
 *
 *  ⚠️ A ROW WITH NO LADDER AND NO `repsMin` HAS NOTHING TO CLIMB, and says so rather than
 *  inventing a starting point — the alternative is a skill row that silently begins progressing
 *  from a number nobody prescribed.
 */
function repLadder(
  name: string,
  prescribed: RulePrescription,
  next: RuleRowPrescription,
  config: RowConfig,
  log: { sets: RuleLoggedSet[] } | null,
  record: (kind: ChangeKind, reason: string) => void,
): void {
  const verdict = performance(prescribed, log);
  if (verdict.kind === "notLogged") {
    record("frozen", `${name}: nothing was logged, so the rep targets stay put.`);
    return;
  }
  if (verdict.kind === "missed") {
    record("frozen", `${name}: ${verdict.why}, so the same rep targets repeat.`);
    return;
  }

  /* The ladder the rule climbs. A row that states a range but no per-set column starts from a flat
     ladder at the range's FLOOR — `repsMin`, because that is what each set was asked for. */
  let ladder = [...next.perSetReps];
  if (ladder.length === 0 && next.repsMin !== null) {
    ladder = Array.from({ length: next.sets }, () => next.repsMin as number);
  }
  if (ladder.length === 0) {
    record("frozen", `${name}: no rep targets to climb.`);
    return;
  }

  const switchReps = config.int("switch_reps");
  if (switchReps !== null && ladder.every((r) => r >= switchReps)) {
    /* §5.3 — the whole ladder is at its target: swap to bodyweight-plus-load and start again.
       ⚠️ A ROW CARRYING ASSISTANCE CAN NEVER TAKE A LOAD — the database forbids the pair — so the
       swap is REFUSED LOUDLY rather than written blind. And the refusal names what it actually is:
       a rung promotion, which is a human decision, not a load step. */
    if (next.assistKg !== null) {
      record("blocked",
        `${name}: the ladder is at its target but the row is assisted, and a prescription cannot ` +
        "carry assistance and load at once. This is a rung promotion, not a load step.");
      return;
    }
    const step = config.double("switch_step_kg") ?? 2.5;
    const before = next.targetWeightKg ?? 0;
    const start = config.ints("start_reps")
      ?? Array.from({ length: next.sets }, () => next.repsMin ?? switchReps);
    next.targetWeightKg = snappedKg(before + step);
    next.perSetReps = resize(start, next.sets);
    syncRepRange(next);
    record("ladderSwitch",
      `${name}: every set reached ${switchReps} reps — adding ${kgText(step)} kg and resetting ` +
      `the ladder to ${next.perSetReps.join(",")}.`);
    return;
  }

  /* +1 rep to ONE set — the LAST set currently sitting at the ladder's low value.
     ⚠️ THE **LAST**, NOT THE FIRST, AND IT IS THE SPEC'S OWN SHAPE: `3,3,3,3,4` → `3,3,3,4,4`.
     Taking the first would give `4,3,3,3,4`, which is the same total and a different session —
     an athlete works up a ladder, not down one. */
  const low = Math.min(...ladder);
  const index = ladder.lastIndexOf(low);
  const beforeLadder = [...ladder];
  ladder[index] = low + 1;
  next.perSetReps = ladder;
  syncRepRange(next);
  record("repIncrease",
    `${name}: ${beforeLadder.join(",")} → ${ladder.join(",")} — one more rep on set ${index + 1}.`);
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * SLICE 6 — `hold_rung` (§5.4), the largest family: 111 lines of Swift and FOUR decisions.
 *
 * ⚠️ IT IS ONE SLICE, NOT TWO, AND HERE IS THE TEST I SAID I WOULD APPLY. The four decisions share
 * one subject — *what may change about a hold, and in what order* — and the answer to every one of
 * them is "not the seconds". Splitting "the band half" from "the sets half" would put the two arms
 * of a single `guard` chain in different commits, and the mutation pass that matters is precisely
 * the one that proves each arm is reached only when the ones before it did not fire.
 * ══════════════════════════════════════════════════════════════════════════════════════════════ */

/** Mirrors `SetEffort.loggedRung`, Services/SetEffort.swift.
 *
 *  ⚠️ A MIXED SESSION, A FOREIGN LABEL OR SILENCE MOVES NOTHING. It answers only when EVERY
 *  completed set carried the SAME label and that label is on THIS ladder — anything else is not
 *  evidence about a rung, and guessing from a majority would move an athlete's ladder on one
 *  mistyped chip. */
export function loggedRung(labels: string[], rungs: string[]): number | null {
  const named = labels.map((l) => l.trim()).filter((l) => l !== "");
  const first = named[0];
  if (first === undefined) return null;
  if (!named.every((l) => l === first)) return null;
  const index = rungs.indexOf(first);
  return index === -1 ? null : index;
}

/** §5.4 — mirrors `V6Progression.swift:1036`.
 *
 *  🔴 **THE ONE THING THAT NEVER HAPPENS HERE IS A LONGER HOLD.** Seconds are a property of the
 *  RUNG; only a test week re-derives them (§5.13). Every arm below says so in its own sentence,
 *  because a coach reading "clean, and it holds" needs to know that is the rule and not a refusal.
 *
 *  THE ORDER IS THE RULE, AND EACH STEP ONLY HAPPENS BECAUSE THE ONES BEFORE IT DID NOT:
 *  1. adopt the rung the athlete was actually ON, if the log says a different one;
 *  2. a non-clean week repeats, unchanged;
 *  3. thin the BAND after `thin_after_sessions` clean sessions — an INDEX move, never arithmetic;
 *  4. else take a kilogram off the assistance;
 *  5. else, and ONLY for this wave's emphasis, buy one more SET.
 */
function holdRung(
  name: string,
  prescribed: RulePrescription,
  next: RuleRowPrescription,
  config: RowConfig,
  log: { sets: RuleLoggedSet[] } | null,
  record: (kind: ChangeKind, reason: string) => void,
  emphasis: string | null,
): void {
  /* ── 1. THE LOGGED RUNG WINS (2026-09-06) ──
     His band reports overrule the sheet's labels: when every completed set was logged on one of
     THIS ladder's rungs and it is not the rung the state says, the ladder MOVES THERE and the
     clean count restarts — **a clean session at M+L is no evidence about S+M+L**. */
  const ladderBefore = config.bandLadder;
  if (ladderBefore !== null && log !== null) {
    const adopted = loggedRung(
      log.sets.filter((s) => s.completed).map((s) => s.bandLabel ?? ""),
      ladderBefore.rungs,
    );
    if (adopted !== null && adopted !== ladderBefore.index) {
      const was = ladderBefore.currentRung;
      config.set("band_index", adopted);
      config.set("clean_sessions", 0);
      record("rungAdopted",
        `${name}: logged on ${ladderBefore.rungs[adopted]}, not ${was} — the ladder moves to ` +
        "where the athlete is, and clean sessions restart there.");
    }
  }

  // ── 2. A non-clean week repeats. BOTH verdicts are `frozen`: there is no load here to hold.
  const verdict = performance(prescribed, log);
  if (verdict.kind === "notLogged") {
    record("frozen", `${name}: nothing was logged, so the hold repeats unchanged.`);
    return;
  }
  if (verdict.kind === "missed") {
    record("frozen", `${name}: ${verdict.why}, so the hold repeats unchanged.`);
    return;
  }

  /* ── 3. THE BAND HALF — §5 rule 4(a).
     ⚠️ A BAND LADDER IS AN ORDERED ENUM AND MUST NEVER BE INTERPOLATED (spec §10a). Its rungs have
     no numeric value — "S+M+L" is not 3 and "M+L" is not 2 — so the step is an INDEX move by
     exactly ONE position, and averaging two rungs is not a thing the domain permits. That is why
     this is a separate arm rather than a cleverly-generalised `assistKg`: giving the band a number
     is the one mistake the spec names by hand.
     ⚠️ AND IT IS RE-READ FROM THE CONFIG, because step 1 may have just moved `band_index`. */
  const ladder = config.bandLadder;
  if (ladder !== null) {
    const earned = (config.int("clean_sessions") ?? 0) + 1;
    const required = Math.max(1, config.int("thin_after_sessions") ?? 2);
    if (earned < required) {
      config.set("clean_sessions", earned);
      record("frozen",
        `${name}: clean session ${earned} of ${required} at ${ladder.currentRung} before the band thins.`);
      return;
    }
    const thinner = ladder.thinner;
    if (thinner === null) {
      /* ⚠️ THE COUNTER IS CLEARED EVEN THOUGH NOTHING MOVED. The athlete is clean at the last
         rung; what they need is a human decision, and leaving the counter climbing would make the
         same message look more and more urgent without changing what it says. */
      config.set("clean_sessions", 0);
      record("blocked",
        `${name}: clean at ${ladder.currentRung}, which is the last rung of the ladder — there is ` +
        "no thinner band, so this is a rung promotion rather than a step and it holds.");
      return;
    }
    config.set("band_index", thinner.index);
    config.set("clean_sessions", 0);
    record("assistanceDecrease",
      `${name}: band ${ladder.currentRung} → ${thinner.rung} — ${required} clean sessions in a ` +
      `row. Thinner is harder. The hold itself is unchanged at ${next.holdSecondsMin ?? 0}s.`);
    return;
  }

  // ── 4. THE KILOGRAM HALF, kept for the rows that still carry one.
  const step = config.double("assist_step_kg") ?? 1.25;
  const assist = next.assistKg;
  if (assist !== null) {
    const target = assist - step;
    if (!(target > 0)) {
      record("blocked",
        `${name}: clean at ${kgText(assist)} kg of assistance — one more step would take it to ` +
        "zero, which is a rung promotion rather than a step, so it holds.");
      return;
    }
    next.assistKg = snappedKg(target);
    record("assistanceDecrease",
      `${name}: assistance ${kgText(assist)} → ${kgText(target)} kg — every hold was clean. The ` +
      `hold itself is unchanged at ${next.holdSecondsMin ?? 0}s.`);
    return;
  }

  /* ── 5. No assistance to take away, so the only dimension left is a SET — and §5.4 allows that
     ONLY for the skill this wave is built around.
     ⚠️ `family === emphasis` WITH BOTH NON-NULL. A null emphasis matches nothing rather than
     everything; a row naming no family is not "every family". */
  const family = config.text("family");
  if (family === null || emphasis === null || family !== emphasis) {
    record("frozen",
      `${name}: clean, but hold seconds never lengthen inside a rung and ` +
      `${family ?? "this skill"} is not this wave's emphasis` +
      (emphasis === null ? "" : ` (${emphasis})`) + " — it holds.");
    return;
  }
  const maximum = config.int("max_sets");
  if (maximum !== null && next.sets >= maximum) {
    record("frozen", `${name}: clean, and already at its ${maximum}-set ceiling for this rung.`);
    return;
  }
  const before = next.sets;
  setSets(next, before + 1);
  record("setAdded",
    `${name}: ${before} → ${next.sets} sets of ${next.holdSecondsMin ?? 0}s — ${emphasis} is this ` +
    "wave's emphasis, so the clean work buys a set rather than a longer hold.");
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * SLICE 7 — `distance_cm` (§5.9) and `box_height_cm` (§5.10). Two small families in one commit
 * because they share a shape and differ in exactly the interesting way: both progress something
 * the ROW's own note states rather than a column the schema has, and one of them **cannot decide
 * by itself** — its step is gated on a quality judgement no log can make, so it arms a QUESTION.
 * ══════════════════════════════════════════════════════════════════════════════════════════════ */

/** Mirrors `V6Progression.plain`, V6Progression.swift:2103.
 *  ⚠️ NOT `kgText`. This one is `%.1f` for a fraction where `AssistLoad.kg` is `%.2f` with the
 *  zeros trimmed — 2.25 prints "2.25" as a load and "2.3" as a distance. Two formatters, two
 *  vocabularies, and using either for the other's number is a wrong string in a coach's sentence. */
export function plainCm(value: number): string {
  if (value % 1 === 0) return String(Math.trunc(value));
  return value.toFixed(1);
}

/** §5.9 — mirrors `V6Progression.swift:1147`.
 *
 *  A lean, a gap, a reach: the progression is a DISTANCE the row's own note states, because no
 *  per-row distance column exists and inventing one would be a column with a single reader. The
 *  value lives in the config and is printed into the prose the athlete reads.
 *
 *  ⚠️ THE REP LADDER COMES FIRST AND THE DISTANCE WAITS FOR IT. Where the row states a
 *  `rep_target`, the distance does NOT move until every set has climbed to it — otherwise an
 *  athlete gets a harder lean AND more reps in the same week, twice over.
 *  ⚠️ AND `rep_target` WITHOUT `rep_start` IS THE TWO-STAGE CONFIG THIS REPO'S `configFaults` LINT
 *  EXISTS FOR — the reset falls back to `repsMin`, then to the target itself, so it can never
 *  restart at zero.
 */
function distanceCm(
  name: string,
  prescribed: RulePrescription,
  next: RuleRowPrescription,
  config: RowConfig,
  log: { sets: RuleLoggedSet[] } | null,
  record: (kind: ChangeKind, reason: string) => void,
): void {
  const step = config.double("step_cm") ?? 1;
  const verdict = performance(prescribed, log);
  if (verdict.kind === "notLogged") {
    record("frozen", `${name}: nothing was logged, so the distance stays where it is.`);
    return;
  }
  if (verdict.kind === "missed") {
    record("frozen", `${name}: ${verdict.why}, so the same distance repeats.`);
    return;
  }

  const target = config.int("rep_target");
  if (target !== null) {
    let ladder = [...next.perSetReps];
    if (ladder.length === 0 && next.repsMin !== null) {
      ladder = Array.from({ length: next.sets }, () => next.repsMin as number);
    }
    if (ladder.length > 0 && ladder.some((r) => r < target)) {
      /* ⚠️ EVERY SET CLIMBS HERE, CLAMPED AT THE TARGET — unlike §5.3, where ONE set climbs. The
         two rules look alike and are not: a rep ladder IS the progression, while these reps are a
         gate in front of one. */
      const before = [...ladder];
      ladder = ladder.map((r) => Math.min(target, r + 1));
      next.perSetReps = ladder;
      syncRepRange(next);
      record("repIncrease",
        `${name}: ${before.join(",")} -> ${ladder.join(",")} reps - the distance holds until ` +
        `every set reaches ${target}.`);
      return;
    }
    const start = config.int("rep_start") ?? next.repsMin ?? target;
    next.perSetReps = Array.from({ length: next.sets }, () => start);
    syncRepRange(next);
  }

  const before = config.double("current_cm") ?? 0;
  config.set("current_cm", before + step);
  const label = config.text("distance_label") ?? "distance";
  record("distanceIncrease",
    `${name}: the ${label} goes ${plainCm(before)} -> ${plainCm(before + step)} cm - every set ` +
    `was clean at ${plainCm(before)} cm` + (target !== null ? ", at the full rep target." : "."));
}

/** §5.10 — mirrors `V6Progression.swift:1218`.
 *
 *  🔴 **THIS RULE CANNOT DECIDE BY ITSELF, AND THAT IS THE DESIGN.** The step is gated on a QUALITY
 *  judgement no log can make — "rings to the navel, held 2 s at the top" — so a clean week ARMS A
 *  QUESTION rather than moving the box. The athlete answers; the NEXT run reads the answer.
 *
 *  ⚠️ THE ANSWER IS THREE-STATE AND ALL THREE DO SOMETHING DIFFERENT: **true** steps and disarms,
 *  **false** disarms and holds — the range was not full, so the step is not earned — and
 *  **undefined keeps waiting**. Collapsing "no" into "not yet" would re-ask a question the athlete
 *  has already answered; collapsing "not yet" into "no" would refuse a step nobody declined.
 *
 *  ⚠️ AND A DISCRETE LADDER IS NOT ARITHMETIC: the next height is the first STEP GREATER THAN the
 *  current one, read off the row's own list. There is no `+5 cm`.
 */
function boxHeightCm(
  name: string,
  prescribed: RulePrescription,
  config: RowConfig,
  log: { sets: RuleLoggedSet[] } | null,
  record: (kind: ChangeKind, reason: string) => void,
  gateAnswer: boolean | undefined,
  question: (text: string) => void,
): void {
  const steps = config.ints("steps") ?? [];
  const current = config.int("current_cm") ?? steps[0] ?? 0;
  const nextStep = steps.find((s) => s > current);

  const verdict = performance(prescribed, log);
  if (verdict.kind === "notLogged") {
    record("frozen", `${name}: nothing was logged, so the height stays at ${current} cm.`);
    return;
  }
  if (verdict.kind === "missed") {
    record("frozen", `${name}: ${verdict.why}, so ${current} cm repeats.`);
    return;
  }

  if (nextStep === undefined) {
    record("frozen",
      `${name}: clean at ${current} cm, which is the top of this ladder - the next step is a ` +
      "harder variation, not a taller box.");
    return;
  }

  const prompt = config.text("gate_question")
    ?? "Did every set reach full range - rings to the navel, held 2 s at the top?";
  const ask = () => question(`${name}: ${prompt} (Answer it and the box goes to ${nextStep} cm.)`);

  if (config.bool("gate_armed") === true) {
    if (gateAnswer === true) {
      config.set("gate_armed", undefined);
      config.set("current_cm", nextStep);
      record("heightIncrease",
        `${name}: ${current} -> ${nextStep} cm - you confirmed full range on every set at ` +
        `${current} cm.`);
      return;
    }
    if (gateAnswer === false) {
      config.set("gate_armed", undefined);
      record("frozen",
        `${name}: staying at ${current} cm - the range was not full on every set, so the step is ` +
        "not earned yet.");
      return;
    }
    ask();
    record("gateArmed",
      `${name}: still waiting on one answer before the box goes to ${nextStep} cm.`);
    return;
  }
  config.set("gate_armed", true);
  ask();
  record("gateArmed",
    `${name}: every set was clean at ${current} cm, so ${nextStep} cm is due - one question first.`);
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * SLICE 8 — `accessory_double` (§5.11): hypertrophy accessories, double progression.
 *
 * THE LAST RULE FAMILY. After this the remaining Swift is the POST-PASSES — §5.5's promotion
 * gates, §5.8's wrist ramp, §5.12's region stops, §5.13's retests and §4's straight-arm budget —
 * every one of which is still refused by name in `portGaps`, so nothing can run on half of them.
 * ══════════════════════════════════════════════════════════════════════════════════════════════ */

/** §5.11 — mirrors `V6Progression.swift:1280`.
 *
 *  DOUBLE PROGRESSION: climb the reps inside a fixed range, and when every set is at the TOP of it,
 *  add load and drop back to the BOTTOM.
 *
 *  🔴 **`perSetReps` HERE IS THE CURRENT UNIFORM TARGET, NOT THE RANGE — AND THAT IS WHY
 *  `syncRepRange` IS NEVER CALLED FROM THIS ARM.** `repsMin`/`repsMax` stay the fixed 12–15 policy
 *  range the athlete's card keeps showing while `perSetReps` walks 12, 13, 14, 15 through it. A
 *  shared mutator that resynced the range would overwrite the policy with whichever single number
 *  the ladder happened to be on — which is exactly what `setSets` used to do, and the note there
 *  is the other half of this one.
 *
 *  ⚠️ EVERY SET CARRIES THE SAME TARGET. This is not §5.3's ladder: there is no "one more rep on
 *  set 4", and writing one would make the range's floor and ceiling meaningless.
 */
function accessoryDouble(
  name: string,
  prescribed: RulePrescription,
  next: RuleRowPrescription,
  config: RowConfig,
  log: { sets: RuleLoggedSet[] } | null,
  record: (kind: ChangeKind, reason: string) => void,
): void {
  /* ⚠️ THE RANGE IS THE CONFIG'S FIRST AND THE ROW'S SECOND. A row whose card says 12–15 while its
     rule says 8–12 is progressing on the rule's numbers, and the `?? next.repsMin` fallback is what
     lets a row state the range once rather than twice. */
  const low = config.int("rep_min") ?? next.repsMin ?? 0;
  const high = config.int("rep_max") ?? next.repsMax ?? low;
  if (!(high >= low && high > 0)) {
    record("frozen", `${name}: no rep range to climb.`);
    return;
  }

  const verdict = performance(prescribed, log);
  if (verdict.kind === "notLogged") {
    record("frozen", `${name}: nothing was logged, so the target reps stay put.`);
    return;
  }
  if (verdict.kind === "missed") {
    record("frozen", `${name}: ${verdict.why}, so the same target repeats.`);
    return;
  }

  const current = config.int("current_reps") ?? next.perSetReps[0] ?? low;
  if (current >= high) {
    const step = config.double("step_kg");
    const weight = next.targetWeightKg;
    if (step === null || weight === null) {
      /* ⚠️ BLOCKED RATHER THAN "ADD A REP ANYWAY". A bodyweight accessory at the top of its range
         has run out of the only dimension this rule owns, and saying so is the honest answer —
         another rep outside the range would quietly redefine the prescription the card shows. */
      record("blocked",
        `${name}: ${high} reps on every set, and there is no load to add — this needs a harder ` +
        "variation rather than another rep.");
      return;
    }
    next.targetWeightKg = snappedKg(weight + step);
    config.set("current_reps", low);
    next.perSetReps = Array.from({ length: next.sets }, () => low);
    /* The §5.1 split once more: the COLUMN is snapped, the SENTENCE is the raw sum. */
    record("loadIncrease",
      `${name}: ${high} reps on every set — ${kgText(weight)} → ${kgText(weight + step)} kg and ` +
      `back to ${low} reps.`);
    return;
  }
  config.set("current_reps", current + 1);
  next.perSetReps = Array.from({ length: next.sets }, () => current + 1);
  record("repIncrease",
    `${name}: ${current} → ${current + 1} reps a set, inside the ${low}–${high} range.`);
}

/* ════════════════════════════════════════════════════════════════════════════════════════════
 * SLICE 9 — §5.13, THE TEST WEEK. The first POST-PASS, and the smallest of the five on purpose:
 * every other post-pass reads what this one produces, so it is the one that has to be right first.
 *
 * TWO FUNCTIONS AND A BAND. `testedMaxima` says what the finished week MEASURED; `redose`
 * re-derives a hold row's prescribed seconds from a fresh measurement. Mirrors
 * V6Progression.swift:1627–1681 (`MARK: - §5.13 the test week`).
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** §5.4's dosing band, as the ONE pair of numbers. Mirrors `V6Progression.doseBandLow/High`,
 *  V6Progression.swift:1632. Low's isometric table works holds at 60–75% of the tested max;
 *  `doseBandNote` reports outside it and `redose` lands in the middle of it, and neither may
 *  carry its own copy of the numbers. */
export const DOSE_BAND_LOW = 0.60;
export const DOSE_BAND_HIGH = 0.75;

/** Mirrors `V6Progression.doseBandMidpoint`, V6Progression.swift:1634.
 *
 *  🔴 COMPUTED FROM THE TWO CONSTANTS, NEVER WRITTEN AS `0.675` — AND THE FIRST REASON I WROTE
 *  HERE FOR THAT WAS FALSE, SO IT IS RECORDED AS THE CORRECTION RATHER THAN QUIETLY REPLACED.
 *  I claimed `(0.60 + 0.75) / 2` and the literal `0.675` were two different doubles that would
 *  disagree on the .5 boundaries. **MEASURED 2026-09-20: they are BIT-IDENTICAL**, both
 *  `3fe599999999999a` (0.67500000000000004441…) — the rounding in the sum lands back on exactly
 *  the literal's double. The test below pins that measurement so nobody re-derives the wrong story.
 *
 *  THE REAL REASON IS DRIFT, NOT BITS. The band is stated ONCE, as a pair of numbers, because
 *  `doseBandNote` and `redose` must never carry their own copies of it (the Swift's own comment at
 *  :1630). A literal midpoint would be a THIRD statement of the same fact, and the day Low's table
 *  is re-read and the band moves, the two named constants would move and the literal would sit
 *  there still prescribing against the old band with nothing red anywhere. */
export const doseBandMidpoint = (DOSE_BAND_LOW + DOSE_BAND_HIGH) / 2;

/** What the finished week MEASURED, keyed by EXERCISE. Mirrors `V6Progression.testedMaxima`,
 *  V6Progression.swift:1648.
 *
 *  A row is a retest when its own `progression_config` says `"test_max": true` — the same
 *  self-describing shape D-V6.5 gives every other rule, so a test session is AUTHORED rather than
 *  inferred from a session name.
 *
 *  ⚠️ THE MEASUREMENT IS THE BEST HOLD THE ROW LOGGED, never the average. A max test is the best
 *  of several attempts, and averaging attempts under-reports a max by construction — which, since
 *  §5.13 re-derives every hold dose from these numbers, would quietly shrink a program.
 *
 *  ⚠️ A ROW THAT WAS NOT LOGGED MEASURES NOTHING, and that is the three-state finding again
 *  (clean / missed / notLogged). A test week nobody trained must not overwrite a real tested max
 *  with silence. Three separate ways of saying nothing are all excluded here and each one matters:
 *  no log row at all, a log whose sets are all incomplete or hold nothing, and a best of ZERO —
 *  the last being the one that would do real damage, because a 0 propagates through `redose` as
 *  "re-dose every working hold on this movement to one second".
 *
 *  ⚠️ KEYED ON `row.facts.id` AND MAX-ED ACROSS ROWS. The measurement belongs to the MOVEMENT,
 *  not to a slot in a day: two rows can test one exercise, and the week measured whichever went
 *  best. */
export function testedMaxima(
  rows: RuleRow[],
  logs: Record<string, { sets: RuleLoggedSet[] }>,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const row of rows) {
    const config = RowConfig.from(row.prescription.progressionConfig as Json | undefined);
    if (config === null || config.bool("test_max") !== true) continue;
    const log = logs[row.id];
    if (log === undefined) continue;
    let best: number | null = null;
    for (const set of log.sets) {
      if (!set.completed || set.holdSeconds === null) continue;
      if (best === null || set.holdSeconds > best) best = set.holdSeconds;
    }
    if (best === null || best <= 0) continue;
    out[row.facts.id] = Math.max(out[row.facts.id] ?? 0, best);
  }
  return out;
}

/** Re-derives a hold row's prescribed seconds from a freshly measured max. Mirrors
 *  `V6Progression.redose`, V6Progression.swift:1666.
 *
 *  Lands on the MIDPOINT of the 60–75% band rather than either end, for the reason K1a settled
 *  for `hypertrophySeconds`: the source states a BAND and nothing states a point inside it, so the
 *  midpoint is the only choice that does not invent a preference nobody published.
 *
 *  ⚠️ A ROW PRESCRIBED IN REPS IS UNTOUCHED — that is what the `holdSecondsMin === null` guard
 *  is for, and it is a statement about meaning rather than a null check: a tested max in SECONDS
 *  says nothing whatever about a rep target, and writing one would be the engine inventing a dose.
 *
 *  ⚠️ `holdSecondsMax` AND `perSetSeconds` FOLLOW ONLY WHERE THEY ALREADY EXIST. Creating a
 *  `holdSecondsMax` here would author a ceiling nobody asked for, and filling an empty
 *  `perSetSeconds` would turn an absent per-set column into a present one — V7-1's rule is that
 *  such a column is COMPLETE or EMPTY, never partial, and never conjured by a rule that was only
 *  asked about the dose. */
export function redose(
  next: RuleRowPrescription,
  testedMaxSeconds: number,
  record: (kind: ChangeKind, reason: string) => void,
  name: string,
): void {
  const before = next.holdSecondsMin;
  if (before === null || before <= 0 || testedMaxSeconds <= 0) return;
  /* Swift: `max(1, Int((testedMaxSeconds * doseBandMidpoint).rounded()))`. `Int(_:)` truncates a
     value that `.rounded()` has already made integral, so the rounding is the whole conversion and
     `roundHalfAwayFromZero` is the faithful twin of it. The floor of 1 is what stops a very small
     measured max prescribing a zero-second hold. */
  const target = Math.max(1, roundHalfAwayFromZero(testedMaxSeconds * doseBandMidpoint));
  /* ⚠️ NO CHANGE IS NOT A CHANGE. A retest that lands on the dose already prescribed must not
     record a `holdRedosed`, or the week's change list claims a movement that never happened — and
     `selectionReason` would then quote it on the athlete's card. */
  if (target === before) return;
  next.holdSecondsMin = target;
  if (next.holdSecondsMax !== null) next.holdSecondsMax = target;
  if (next.perSetSeconds.length > 0) {
    next.perSetSeconds = Array.from({ length: next.perSetSeconds.length }, () => target);
  }
  /* ⚠️ `%.0f` IN C ROUNDS HALF-TO-EVEN WHILE `toFixed(0)` ROUNDS HALF-UP, so the two disagree on
     exactly the `.5` values — and they cannot disagree here, because the Swift builds
     `testedMaxSeconds` as `Double(best)` from an `Int` hold and the value is therefore always
     integral. Written down rather than left to luck: a caller handing this function 12.4 is the
     one way that stops being true, and it would show up as a sentence quoting a different number
     from the one it prescribed. */
  record("holdRedosed",
    `${name}: ${before}s → ${target}s — your retested max is ${testedMaxSeconds.toFixed(0)}s, ` +
    `and working holds sit at ${(DOSE_BAND_LOW * 100).toFixed(0)}–` +
    `${(DOSE_BAND_HIGH * 100).toFixed(0)}% of it. A retest is the one thing that ends a rung.`);
}

/* ════════════════════════════════════════════════════════════════════════════════════════════
 * SLICE 10B — `prose`, THE SENTENCE THE ATHLETE READS.
 *
 * Ported now rather than with §5.5 because §5.5 CANNOT LAND WITHOUT IT — the row it introduces
 * takes the TARGET's rule and must carry the target's sentence (V6Progression.swift:1536) — and
 * because slice 2 left the orchestrator's own regeneration named and unbuilt for exactly one
 * stated reason: the rules whose sentences it writes did not exist yet. All eight do now.
 *
 * 🔴 WHY THE SENTENCE IS DERIVED AND NOT STORED, in the Swift's own words (V6Progression.swift:1996):
 * `progression_rule` is what an ATHLETE reads and `progression_config` is what the BUILDER reads,
 * so deriving the first from the second means **they cannot drift into being two statements of one
 * thing** — this repository's defining failure, stated as a design rule by the engine itself. It
 * also satisfies validator rule 25, which requires main and accessory rows to carry a progression
 * rule at all.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** Mirrors `V6Progression.prose`, V6Progression.swift:2000.
 *
 *  ⚠️ EVERY DEFAULT HERE IS THE RULE'S OWN DEFAULT AND MUST MATCH IT EXACTLY. This function and
 *  the rule that acts are two readers of one config: if `prose` says "Add 2.5 kg a week" while
 *  `barbellLinear` steps by 5, the card and the training disagree and nothing turns red. The
 *  defaults are transcribed from the Swift's `prose`, which takes them from the same places the
 *  rules do — and where a rule ever changes its default, THIS function changes in the same commit.
 *
 *  ⚠️ `kgText` FOR LOADS, `plainCm` FOR DISTANCES, never one for the other: 2.25 prints "2.25" as
 *  a load and "2.3" as a distance, and the comment on `plainCm` is the reason. */
export function prose(config: RowConfig, exerciseName: string): string {
  switch (config.rule) {
    case "barbell_linear": {
      const step = kgText(config.double("step_kg") ?? 2.5);
      const hold = config.int("hold_weeks") ?? 2;
      const bar = kgText(config.double("min_rir") ?? 2);
      return `Add ${step} kg a week while every set finishes with at least ${bar} reps in ` +
        `reserve. Two missed jumps in a row → hold the load for ${hold} weeks, then resume.`;
    }
    case "weighted_bw_biweekly": {
      const step = kgText(config.double("step_kg") ?? 2.5);
      const every = config.int("every_weeks") ?? 2;
      return `Add ${step} kg after every ${every} clean weeks.`;
    }
    case "rep_ladder": {
      const switchReps = config.int("switch_reps");
      const step = kgText(config.double("switch_step_kg") ?? 2.5);
      const tail = switchReps === null
        ? ""
        : ` Once every set reaches ${switchReps} reps, add ${step} kg and reset the ladder.`;
      return "Add one rep to a single set each week." + tail;
    }
    case "hold_rung": {
      /* The prose follows whichever assistance dimension the row ACTUALLY CARRIES, because this
         string is what the athlete reads on the card. Telling somebody on a band that a clean week
         takes 1.25 kg off is a sentence about equipment they retired. */
      const ladder = config.bandLadder;
      if (ladder !== null) {
        const required = Math.max(1, config.int("thin_after_sessions") ?? 2);
        const thinner = ladder.thinner;
        const next = thinner === null ? "this is the last rung" : `the next is ${thinner.rung}`;
        return `Hold seconds stay put inside this rung. ${required} clean sessions in a row ` +
          `thins the band one step — thinner is harder. You are on ${ladder.currentRung}; ${next}.`;
      }
      const step = kgText(config.double("assist_step_kg") ?? 1.25);
      return `Hold seconds stay put inside this rung. A clean week takes ${step} kg off the ` +
        "assistance; when there is none left, a set is added only while this skill is the wave's " +
        "emphasis.";
    }
    case "accessory_double": {
      const low = config.int("rep_min") ?? 0;
      const high = config.int("rep_max") ?? 0;
      const stepKg = config.double("step_kg");
      const step = stepKg === null
        ? ""
        : ` At ${high} on every set, add ${kgText(stepKg)} kg and drop back to ${low}.`;
      return `Add a rep a week inside ${low}–${high}.` + step;
    }
    case "distance_cm": {
      /* ⚠️ A NEGATIVE STEP IS A REAL CONFIGURATION, not a mistake to guard against: the deep
         chest-to-wall press progresses by the head-stop box coming DOWN. So the sentence says
         which WAY it goes rather than always saying "add". */
      const raw = config.double("step_cm") ?? 1;
      const label = config.text("distance_label") ?? "distance";
      const currentCm = config.double("current_cm");
      const current = currentCm === null ? "" : `The ${label} is at ${plainCm(currentCm)} cm. `;
      const move = raw < 0 ? `Take ${plainCm(-raw)} cm off` : `Add ${plainCm(raw)} cm`;
      const target = config.int("rep_target");
      if (target === null) {
        return current + `${move} once every set is clean at this distance.`;
      }
      const repStart = config.int("rep_start");
      const start = repStart === null ? "" : ` and the reps go back to ${repStart}`;
      return current + "Add a rep to every set each clean week until they are all at " +
        `${target}; then ${move.toLowerCase()}${start}.`;
    }
    case "box_height_cm": {
      const steps = config.ints("steps") ?? [];
      const current = config.int("current_cm") ?? steps[0] ?? 0;
      const ladder = steps.length === 0
        ? ""
        : ` Ladder: ${steps.map(String).join(" → ")} cm.`;
      return `Currently ${current} cm.${ladder} A clean week earns the next step, once you ` +
        "confirm the range was full on every set.";
    }
    case "marker":
      return "Logged, never auto-progressed.";
    case null:
      /* ⚠️ TWO DIFFERENT SILENCES, AND THEY MUST NOT COLLAPSE. No rule at all returns "" — which
         is what the caller's non-empty guard reads as "leave the coach's own sentence alone". A
         rule NAMED but unrecognised returns a sentence SAYING SO, because a row whose rule this
         build cannot apply is a row that will silently stop progressing, and the athlete's card is
         the only place that fact is visible. */
      return config.ruleName === ""
        ? ""
        : `${exerciseName} carries the progression rule "${config.ruleName}", which this build ` +
          "does not apply.";
  }
}

/* ════════════════════════════════════════════════════════════════════════════════════════════
 * SLICE 10C — §5.5, THE PROMOTION GATES. The first pass that CREATES a row.
 *
 * "Advancement replaces ONE set at a time: the new rung takes one set slot; another slot converts
 * every 4–6 weeks." Mirrors `V6Progression.applyPromotionGates`, V6Progression.swift:1355.
 *
 * ⚠️ THE GATE IS THE ROW'S OWN, NOT THE PROGRAM'S. `docs/V6-SEED-PLAN.md` §3 sketched a `gates`
 * list in `engine_config`; it lives in the PARENT ROW's `progression_config` instead, because
 * D-V6.5's whole shape is that a row is self-describing and survives a swap. A program-level list
 * keyed by exercise id would be a second vocabulary for "which rung is this athlete on", and the
 * row already answers that question.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** §5.5 — mirrors `V6Progression.applyPromotionGates`, V6Progression.swift:1355.
 *
 *  🔴 A PAIRED ROW IS REFUSED, LOUDLY — AND THE REASON IS **NOT** THE ONE THE SWIFT'S COMMENT
 *  OPENS BY QUOTING, WHICH IS WHY THE CORRECTION IS TRANSCRIBED AND NOT THE QUOTE. That comment
 *  used to say "taking a set from one member of a superset breaks validator rule 14's equal-sets
 *  requirement". Measured in session V6-5: **rule 14 has no equal-sets clause.** It checks three
 *  things — a straight-sets row carries no group id, a superset group is exactly TWO rows ordered
 *  {1,2}, a circuit has at least two — and it never reads `sets` at all. Equal sets is
 *  `SupersetPlanner.canPair`'s PRECONDITION, a constraint on what the ENGINE will build, not a
 *  rule about what is valid; the player deliberately tolerates unequal members (`rounds` is the
 *  MAXIMUM and a short member drops out of the later rounds).
 *
 *  THE REAL REASON THE REFUSAL IS RIGHT is the clause it guards: the introduced row is a COPY of
 *  the parent, so it inherits `formatGroupId`, and a THIRD row in a superset group is exactly what
 *  rule 14 rejects — critically. That is a fact about THIS FUNCTION, not about set counts. The
 *  spec's gated rows are all straight-sets work, so the refusal costs nothing today and says what
 *  it refused rather than writing a week a human must unpick.
 *
 *  ⚠️ REOPEN CONDITION, EXACT, carried across verbatim: clearing `formatGroupId` (and the Swift's
 *  `formatGroupOrder`, which this twin does not carry) on the introduced row and setting it to
 *  straight sets would let a paired row promote — the parent keeps a two-member group with one
 *  fewer set, which is legal and renders. That is a measured change with its own before/after, not
 *  a comment fix, and it is NOT taken here.
 */
export function applyPromotionGates(
  input: RuleInput,
  planned: PlannedRow[],
  changes: Change[],
  verdicts: Record<string, Performance>,
  questions: string[],
  warnings: string[],
): { planned: PlannedRow[]; changes: Change[] } {
  const out = [...planned];
  const outChanges = [...changes];
  const byId = new Map(input.rows.map((r) => [r.id, r] as const));

  /* ⚠️ SNAPSHOTTED BEFORE ANYTHING MOVES (V6Progression.swift:1360): a gate that OPENS this week
     must not then also convert a slot in the same week off its own freshly-written counter. The
     list is also why the loop re-finds its index every iteration — rows are APPENDED as gates
     open, so a position captured now would point at the wrong row later. */
  const opened = out.map((p) => p.sourceRowId);

  for (const parentId of opened) {
    const index = out.findIndex((p) => p.sourceRowId === parentId);
    if (index < 0) continue;
    const entry = out[index];
    const parent = byId.get(parentId);
    if (entry === undefined || parent === undefined) continue;
    if (entry.introducedFacts !== null) continue;

    const config = RowConfig.from(entry.prescription.progressionConfig as Json | undefined);
    if (config === null) continue;
    const gateRaw = config.raw["gate"];
    if (gateRaw === null || gateRaw === undefined
        || typeof gateRaw !== "object" || Array.isArray(gateRaw)) continue;
    const gateConfig = RowConfig.fromRaw(gateRaw as Record<string, Json>);
    const name = parent.facts.name;
    let prescription: RuleRowPrescription = { ...entry.prescription };

    const writeBack = () => {
      config.set("gate", gateConfig.json);
      const at = out.findIndex((p) => p.sourceRowId === parentId);
      if (at < 0) return;
      out[at] = {
        ...out[at]!,
        prescription: { ...prescription, progressionConfig: config.json },
      };
    };
    const refuse = (why: string) => {
      outChanges.push({ rowId: parentId, exerciseName: name, kind: "blocked",
                        reason: `${name}: ${why}` });
      writeBack();
    };

    // ── already introduced: the 4–6 week slot conversion ──────────────────────────────────
    if (gateConfig.bool("introduced") === true) {
      const targetId = gateConfig.text("target_exercise_id");
      if (targetId === null) continue;
      const waited = (gateConfig.int("weeks_since_conversion") ?? 0) + 1;
      const cadence = Math.max(1, gateConfig.int("convert_every_weeks") ?? 5);
      gateConfig.set("weeks_since_conversion", waited);
      if (waited < cadence) { writeBack(); continue; }
      if (prescription.sets <= 1) {
        /* The parent is down to its last set; the rung has FULLY CONVERTED and the gate has
           nothing left to do. Said once, by resetting the counter, not every week. */
        gateConfig.set("weeks_since_conversion", 0);
        writeBack();
        continue;
      }
      const siblingIndex = out.findIndex((p) =>
        p.prescription.exerciseId === targetId
        && p.prescription.programSessionId === prescription.programSessionId);
      if (siblingIndex < 0) {
        refuse("the harder rung this row promotes into is not in the week any more, so there is "
               + "nothing to convert another set into.");
        continue;
      }
      const parentBefore = prescription.sets;
      setSets(prescription, parentBefore - 1);
      const sibling = { ...out[siblingIndex]!.prescription };
      const siblingBefore = sibling.sets;
      setSets(sibling, siblingBefore + 1);
      out[siblingIndex] = { ...out[siblingIndex]!, prescription: sibling };
      gateConfig.set("weeks_since_conversion", 0);
      const siblingName = out[siblingIndex]!.introducedFacts?.name
        ?? byId.get(out[siblingIndex]!.sourceRowId)?.facts.name
        ?? "the harder rung";
      outChanges.push({
        rowId: parentId, exerciseName: name, kind: "rungSlotConverted",
        reason: `${name} ${parentBefore} → ${prescription.sets} sets and ${siblingName} `
          + `${siblingBefore} → ${sibling.sets} — another slot converts to the harder rung every `
          + `${cadence} weeks.`,
      });
      writeBack();
      continue;
    }

    // ── not yet introduced: does the gate open? ───────────────────────────────────────────
    //
    // 🔴 THE REQUIREMENTS ARE READ OFF THE WEEK THAT WAS TRAINED, NOT THE ONE BEING BUILT, and
    // the Swift says a control caught this the first time it ran (V6Progression.swift:1431).
    // §5.5's gates are all statements about PERFORMANCE — "assisted planche AT 5 KG for 4×4s
    // clean" — so the evidence is `parent.prescription`, what the athlete actually did. The
    // planned row has already had this week's progression applied, so an athlete clean at
    // 6.25 kg has their assistance stepped to 5 kg one pass earlier and then reads as having met
    // a gate they have never trained at. ⚠️ The MUTATION below still targets the PLANNED row:
    // the slot it gives up is next week's.
    const performed = parent.prescription;
    if (verdicts[parentId]?.kind !== "clean") continue;
    const needSets = gateConfig.int("requires_sets");
    if (needSets !== null && performed.sets < needSets) continue;
    const needHold = gateConfig.int("requires_hold_seconds");
    if (needHold !== null && (performed.holdSecondsMin ?? 0) < needHold) continue;
    const needReps = gateConfig.int("requires_reps");
    if (needReps !== null) {
      const lowest = performed.perSetReps.length > 0
        ? Math.min(...performed.perSetReps)
        : (performed.repsMin ?? 0);
      if (lowest < needReps) continue;
    }
    const ceiling = gateConfig.double("max_assist_kg");
    if (ceiling !== null && (performed.assistKg ?? 0) > ceiling) continue;

    /* ── the confirmation half, for the gates whose evidence is a JUDGEMENT ──────────────────
       "half-lay straddle FL 4×3s clean, HIPS LEVEL" is two conditions and only the first is in
       the log. ⚠️ THE SAME THREE-STATE RULE AS §5.10's ROM GATE: an UNANSWERED question HOLDS and
       only a FALSE refuses, because "nobody was asked yet" and "the athlete said the quality was
       not there" want opposite treatments. */
    const prompt = gateConfig.text("requires_confirmation");
    if (prompt !== null) {
      if (gateConfig.bool("gate_armed") !== true) {
        gateConfig.set("gate_armed", true);
        questions.push(`${name}: ${prompt}`);
        outChanges.push({ rowId: parentId, exerciseName: name, kind: "gateArmed",
                          reason: `${name}: the numbers are there for the next rung — one `
                            + "question first." });
        writeBack();
        continue;
      }
      const answer = parentId in input.gateAnswers ? input.gateAnswers[parentId] : undefined;
      if (answer === false) {
        gateConfig.set("gate_armed", undefined);
        outChanges.push({ rowId: parentId, exerciseName: name, kind: "frozen",
                          reason: `${name}: the promotion is not earned yet — the quality was `
                            + "not there." });
        writeBack();
        continue;
      }
      if (answer === undefined) {
        questions.push(`${name}: ${prompt}`);
        writeBack();
        continue;
      }
      gateConfig.set("gate_armed", undefined);
    }

    if (prescription.sets <= 1) {
      refuse("the next rung is earned, but this row is down to one set and a promotion takes a "
             + "set SLOT — there is nothing to give.");
      continue;
    }
    if (prescription.formatGroupId !== null) {
      warnings.push(`${name} has earned its next rung, and it is one half of a paired set. The `
        + "new rung would join the pair as a THIRD row, which is a shape the app refuses, so the "
        + "promotion is refused rather than written — this one needs a person.");
      refuse("the next rung is earned but this row is paired, and the new rung would enter that "
             + "pair as a third row.");
      continue;
    }
    const targetId = gateConfig.text("target_exercise_id");
    const targetName = gateConfig.text("target_name");
    const targetFacts: ExerciseFacts | null = targetId === null
      ? null
      : input.catalogue[targetId]
        ?? (targetName === null
          ? null
          : { id: targetId, name: targetName, isStraightArm: parent.facts.isStraightArm,
              loadsGrip: null, loadsPronationSupination: null, painRegions: [] });
    if (targetId === null || targetFacts === null) {
      refuse("the next rung is earned but its exercise is not named in the catalogue this build "
             + "was given, so there is nothing to introduce.");
      continue;
    }
    /* §5.8's block reaches here too: a promotion is the ONE place a new exercise enters the
       program, so it is the place "rotation stays blocked" actually bites. ⚠️ §5.8 itself is not
       ported, but THIS clause is part of §5.5 and is transcribed with it — leaving it out would
       introduce a forearm-rotation row to an athlete reporting wrist pain. */
    if (targetFacts.loadsPronationSupination === true && wristPainReported(input)) {
      refuse("the next rung loads forearm rotation and the wrist gate is not open, so it is not "
             + "introduced this week.");
      continue;
    }

    const parentBefore = prescription.sets;
    setSets(prescription, parentBefore - 1);

    const introduced: RuleRowPrescription = { ...prescription };
    introduced.id = input.mint();
    introduced.exerciseId = targetId;
    introduced.assistKg = gateConfig.double("target_assist_kg");
    introduced.targetWeightKg = null;
    introduced.holdSecondsMin = gateConfig.int("target_hold_seconds") ?? prescription.holdSecondsMin;
    introduced.holdSecondsMax = introduced.holdSecondsMin;
    introduced.perSetReps = [];
    introduced.perSetWeightKg = [];
    introduced.perSetSeconds = [];
    introduced.perSetRestSeconds = [];
    introduced.perSetTypes = [];
    introduced.selectionReason = "";
    introduced.notes = "";
    /* ⚠️ A BRAND-NEW RUNG CARRIES THE TARGET'S OWN RULE AND **NONE** OF THE PARENT'S STATE —
       the parent's clean-week counters, its rehab fraction and its own gate all describe a
       DIFFERENT EXERCISE, and inheriting them would have the new rung promoting itself on
       evidence it never earned. */
    const targetConfigRaw = gateConfig.raw["target_config"];
    const targetConfig: Record<string, Json> =
      targetConfigRaw !== null && targetConfigRaw !== undefined
      && typeof targetConfigRaw === "object" && !Array.isArray(targetConfigRaw)
        ? { ...(targetConfigRaw as Record<string, Json>) }
        : { rule: config.ruleName };
    introduced.progressionConfig = targetConfig;
    introduced.progressionRule = prose(RowConfig.fromRaw(targetConfig), targetFacts.name);
    setSets(introduced, 1);

    gateConfig.set("introduced", true);
    gateConfig.set("weeks_since_conversion", 0);
    const reason = `${targetFacts.name} enters at 1 set, taking a slot from ${name} `
      + `(${parentBefore} → ${prescription.sets}) — you hit the gate for it. Another slot `
      + `converts every ${Math.max(1, gateConfig.int("convert_every_weeks") ?? 5)} weeks.`;
    introduced.selectionReason = reason;
    writeBack();
    out.push({ sourceRowId: parentId, prescription: introduced, introducedFacts: targetFacts });
    outChanges.push({ rowId: parentId, exerciseName: targetFacts.name, kind: "rungIntroduced",
                      reason });
  }

  return { planned: out, changes: outChanges };
}

/* ════════════════════════════════════════════════════════════════════════════════════════════
 * SLICE 11 — §5.8, THE WRIST-REHAB RAMP. Re-enter at 40% of former volume, +20% a week
 * pain-free; a flare cuts and then climbs more slowly; forearm rotation stays blocked until two
 * clean hang weeks. Mirrors V6Progression.swift:1716.
 *
 * ⚠️ "VOLUME" IS READ AS **SETS**, and the Swift states that as an approximation rather than the
 * only possible one: sets × reps or sets × seconds would make the ramp depend on a rep target the
 * row's own rule is simultaneously moving, so two rules would be reading each other's output. Sets
 * is the dimension the ramp can own outright, and it is the dimension the spec's acceptance check
 * measures ("grip volume drops 20–40%").
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** Mirrors `V6Progression.RehabVerdict`, V6Progression.swift:1687. */
export interface RehabVerdict {
  /** True when §5.8 is the AUTHORITY on this row's dose this week — so neither §5.12's general
   *  pain freeze, nor the row's own progression rule, nor §5.7's novelty taper may also move it.
   *  Two rules deciding one set count is how a program ends up fighting itself. */
  ownsThisRow: boolean;
  suspendsProgression: boolean;
  warning: string | null;
}

/** Mirrors `V6Progression.rampedSets`, V6Progression.swift:1836.
 *  ⚠️ NEVER BELOW ONE SET: a prescription of zero sets is not a lighter row, it is a row the
 *  database refuses and the athlete never sees. */
export function rampedSets(full: number, fraction: number): number {
  return Math.max(1, roundHalfAwayFromZero(full * fraction));
}

/** §5.8 — mirrors `V6Progression.applyWristRehab`, V6Progression.swift:1716.
 *
 *  ⚠️ THE BAND'S MIDPOINT, NOT ITS END. The spec states −20–40% for a flare and +10–15% for the
 *  climb back, and nothing in it or in the literature picks a point inside either band. Both
 *  defaults are the midpoint and both are overridable from the row's own config — the same answer
 *  K1a's `hypertrophySeconds` gives, and the same one §5.13's `redose` gives: a stated band
 *  answered at its middle is a READING, an unstated end is an INVENTION.
 *
 *  🔴 AN UNASSESSED ROW IS REPORTED, NEVER RAMPED. `loadsGrip` is null on every catalogue row the
 *  v6 seed has not touched, and null means NOBODY HAS SAID. Ramping on that would cut work on
 *  evidence that does not exist; treating null as `true` would cut every row in the program the
 *  first time a wrist hurt. */
export function applyWristRehab(
  row: RuleRow,
  next: RuleRowPrescription,
  config: RowConfig,
  painReported: boolean,
  painFreeWeek: boolean,
  record: (kind: ChangeKind, reason: string) => void,
): RehabVerdict {
  const verdict: RehabVerdict = { ownsThisRow: false, suspendsProgression: false, warning: null };
  const name = row.facts.name;

  /* ⚠️ MAIN-ROLE SECTIONS ONLY, the same decision the deload takes and for the same reason. The
     athlete's WRIST PREP is a grip-loading warm-up drill, and it IS the rehab — cutting it 40%
     because the wrist hurt would take away the one thing in the week that is there to fix it.
     "Grip volume" in the spec's own acceptance check is WORKING volume. */
  if (row.sectionRole !== "main") return verdict;

  /* ── the outright block, which is a FREEZE and never a deletion ──
     The spec says rotation work "stays blocked" and §3 records that none is in the program today,
     so the honest reading is a refusal to INTRODUCE one — §5.5 enforces that half. A row that is
     nonetheless present is frozen and NAMED OUT LOUD: silently dropping an athlete's row would
     remove it from next week's inputs and it could never come back. */
  if (row.facts.loadsPronationSupination === true) {
    const streak = painFreeWeek ? (config.int("wrist_pain_free_weeks") ?? 0) + 1 : 0;
    config.set("wrist_pain_free_weeks", streak);
    const required = config.int("wrist_clear_weeks") ?? 2;
    if (streak < required) {
      verdict.ownsThisRow = true;
      verdict.suspendsProgression = true;
      verdict.warning = `${name} loads forearm rotation and the wrist gate is not open `
        + `(${streak} of ${required} pain-free hang weeks logged). It is frozen, not removed — `
        + "a row this build drops can never come back.";
      record("rehabBlocked",
        `${name}: held where it is — forearm rotation stays blocked until ${required} `
        + `consecutive pain-free weeks with the hang logged (${streak} so far).`);
      return verdict;
    }
  }

  if (row.facts.loadsGrip !== true) {
    if (painReported && row.facts.loadsGrip === null
        && !row.facts.painRegions.includes(WRIST_REGION)) {
      /* Silent on purpose: an unassessed row that does not train the painful region is not a gap
         the ramp cares about. */
      return verdict;
    }
    if (painReported && row.facts.loadsGrip === null) {
      verdict.warning = `${name} has no answer for loads_grip, so the wrist ramp cannot tell `
        + "whether it belongs to it. Nil is UNASSESSED, not false — the row is left exactly as "
        + "it was.";
    }
    return verdict;
  }

  const flareDrop = config.double("flare_drop") ?? 0.30;     // midpoint of −20–40%
  const flareClimb = config.double("flare_climb") ?? 0.125;  // midpoint of +10–15%
  const rampClimb = config.double("ramp_climb") ?? 0.20;     // the spec states +20% exactly
  const entry = config.double("rehab_entry") ?? 0.40;        // "re-enter at 40%"

  /* ⚠️ THE FULL DOSE IS CAPTURED THE FIRST TIME THE RAMP TOUCHES THE ROW AND NEVER RECAPTURED —
     recapturing it from an already-ramped week would ratchet the target down to whatever the ramp
     had cut it to, and the row would never find its way back. */
  const full = config.int("rehab_full_sets") ?? next.sets;
  let fraction = config.double("rehab_fraction") ?? 1.0;

  if (painReported) {
    const before = fraction;
    fraction = Math.max(entry * 0.5, (fraction < 1.0 ? fraction : 1.0) * (1 - flareDrop));
    config.set("rehab_full_sets", full);
    config.set("rehab_fraction", fraction);
    config.set("rehab_state", "flared");
    const sets = rampedSets(full, fraction);
    const beforeSets = next.sets;
    setSets(next, sets);
    verdict.ownsThisRow = true;
    verdict.suspendsProgression = true;
    /* ⚠️ `halfToEven` IS THE `%.0f` TWIN AND IT IS LOAD-BEARING HERE, not pedantry: the default
       `flareClimb` is 0.125, so `climb * 100` is EXACTLY 12.5 — C prints "12" (half to even) and
       `toFixed(0)` prints "13". One of the two languages would tell the athlete a different
       number from the other, on the DEFAULT configuration. */
    record("rehabFlare",
      `${name}: ${beforeSets} → ${sets} sets — you reported wrist pain, so grip volume drops to `
      + `${halfToEven(fraction * 100)}% of the full ${full} (was ${halfToEven(before * 100)}%). `
      + `It climbs back ${halfToEven(flareClimb * 100)}% a week once the week is pain-free.`);
    return verdict;
  }

  if (fraction >= 1.0) return verdict;   // not ramping, nothing to do

  const flared = config.text("rehab_state") === "flared";
  const climb = flared ? flareClimb : rampClimb;
  /* ⚠️ THE SWIFT BINDS `let before = fraction` HERE AND NEVER READS IT (V6Progression.swift:1806)
     — its climb sentence quotes `climb`, the NEW fraction and `full`, not the old one. The binding
     is dropped rather than transcribed because `noUnusedLocals` refuses it, and it is named here
     so the omission reads as deliberate rather than as a line lost in transcription. Nothing
     behavioural rides on it. */
  fraction = Math.min(1.0, fraction * (1 + climb));
  const beforeSets = next.sets;
  const sets = rampedSets(full, fraction);
  setSets(next, sets);
  verdict.ownsThisRow = true;
  verdict.suspendsProgression = true;
  if (fraction >= 1.0) {
    /* The ramp is over and TAKES ITS OWN STATE WITH IT, so next week the row's ordinary
       progression rule owns it again. Leaving the keys behind would make a FINISHED ramp
       indistinguishable from a ramp sitting at its ceiling. */
    config.set("rehab_fraction", undefined);
    config.set("rehab_state", undefined);
    config.set("rehab_full_sets", undefined);
    record("rehabRamp",
      `${name}: back to its full ${full} sets — the wrist ramp is finished and normal `
      + "progression resumes next week.");
  } else {
    config.set("rehab_full_sets", full);
    config.set("rehab_fraction", fraction);
    record("rehabRamp",
      `${name}: ${beforeSets} → ${sets} sets — a pain-free week, so grip volume climbs `
      + `${halfToEven(climb * 100)}% to ${halfToEven(fraction * 100)}% of the full ${full}.`);
  }
  return verdict;
}

/* ════════════════════════════════════════════════════════════════════════════════════════════
 * SLICE 12 — §5.12, THE AUTOREGULATION STOPS: the region freeze, the two counters, and the three
 * proposals derived from them. Mirrors V6Progression.swift:1562 and :1861.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** What counts as the program MOVING FORWARD. Mirrors `V6Progression.progressingKinds`,
 *  V6Progression.swift:1879.
 *
 *  ⚠️ A FREEZE, A HOLD, A REFUSAL AND A HALVED DELOAD ARE ALL DELIBERATELY ABSENT — none of them
 *  is progress, and §5.12's four-week termination proposal exists precisely to notice a program
 *  that only ever does those. Adding one of them here would silence the check this list feeds. */
export const PROGRESSING_KINDS: readonly ChangeKind[] = [
  "loadIncrease", "repIncrease", "ladderSwitch", "assistanceDecrease", "setAdded",
  "distanceIncrease", "heightIncrease", "rungIntroduced", "rungSlotConverted", "holdRedosed",
];

/** The two per-row counters §5.12's stops are derived from. Mirrors
 *  `V6Progression.updateStopCounters`, V6Progression.swift:1861.
 *
 *  🔴 NEITHER MOVES ON AN UNLOGGED WEEK, AND THAT IS THE WHOLE REASON `Performance` HAS THREE
 *  CASES. A holiday is not two failed sessions and it is not four weeks without progress; a build
 *  that counted it as either would meet an athlete returning from two weeks away with a proposal
 *  to abandon their block.
 *
 *  Only a ruled, non-marker row keeps them: a warm-up with no progression rule cannot FAIL to
 *  progress, and giving it a counter would also give it a `progression_config` it never had.
 *
 *  ⚠️ `consecutive_misses` IS NOT `consecutive_fails`, AND THEY MUST NOT BE MERGED. They sit side
 *  by side in the same config object and look like a duplicate; they are two measurements of two
 *  different things. `consecutive_fails` is §5.1's own, counted only on the barbell arm and only
 *  against its ≥2 RIR bar — a MISSED JUMP, a statement about whether the load was ready to move.
 *  `consecutive_misses` is §5.12's, counted on every ruled row against the prescription alone — a
 *  MISSED SESSION, a statement about the athlete. A squat completed at RIR 0 is a missed jump AND
 *  a clean session, and folding the two would either stall the load on a hard week or propose a
 *  light week for an athlete who did everything asked. */
export function updateStopCounters(
  config: RowConfig, verdict: Performance, changes: Change[],
): void {
  const rule = config.rule;
  if (rule === null || rule === "marker") return;
  if (verdict.kind === "notLogged") return;
  if (verdict.kind === "missed") {
    config.set("consecutive_misses", (config.int("consecutive_misses") ?? 0) + 1);
  } else {
    config.set("consecutive_misses", 0);
  }
  const progressed = changes.some((c) => PROGRESSING_KINDS.includes(c.kind));
  config.set("weeks_without_progress",
             progressed ? 0 : (config.int("weeks_without_progress") ?? 0) + 1);
}

/** §5.12's three proposals. Mirrors `V6Progression.stops`, V6Progression.swift:1570. */
export function stops(planned: PlannedRow[], input: RuleInput): Stop[] {
  /* ⚠️ A DELOAD PROPOSES NOTHING. Its rows are halved by design, so every counter reads exactly
     the way a stalling program does and every stop would fire on a week that is working. */
  if (input.isDeload) return [];
  const out: Stop[] = [];
  const byId = new Map(input.rows.map((r) => [r.id, r] as const));

  /* (1) "Two consecutive sessions of the same day failing to match prior performance."
     ⚠️ MEASURED ON THE ROW, NOT ON A DAY INDEX: a v6 row appears once a week on one day, so two
     consecutive weeks in which THAT row missed IS two consecutive sessions of that day — and this
     module does not carry a day index and would have to be told one. */
  const stalling: string[] = [];
  const progressless: number[] = [];
  for (const plan of planned) {
    if (plan.introducedFacts !== null) continue;
    const row = byId.get(plan.sourceRowId);
    if (row === undefined) continue;
    const config = RowConfig.from(plan.prescription.progressionConfig as Json | undefined);
    if (config === null) continue;
    const rule = config.rule;
    if (rule === null || rule === "marker") continue;
    if ((config.int("consecutive_misses") ?? 0) >= 2) stalling.push(row.facts.name);
    progressless.push(config.int("weeks_without_progress") ?? 0);
  }
  if (stalling.length > 0) {
    out.push({
      kind: "lightWeekProposed",
      message: `Two weeks running, ${[...stalling].sort().join(" and ")} came up short of what `
        + "was asked. That usually means fatigue rather than a bad program — consider an "
        + "unscheduled light week before the next one.",
    });
  }

  /* (2) "No progress ANYWHERE for 4 weeks." ⚠️ **ANYWHERE** IS THE LOAD-BEARING WORD: one row
     stalling is normal, and the proposal only earns its place when nothing in the program has
     moved. `min` over the counters is exactly that reading — and an EMPTY program cannot satisfy
     it, because the minimum of nothing is not a number. */
  if (progressless.length > 0) {
    const quietest = Math.min(...progressless);
    if (quietest >= 4) {
      out.push({
        kind: "cycleTerminationProposed",
        message: `Nothing in the program has moved forward for ${quietest} logged weeks. This `
          + "block has probably given what it has — consider finishing it with a test week and "
          + "starting a new one.",
      });
    }
  }

  /* (3) The pain alert. ⚠️ NAMED SEPARATELY FROM THE FREEZE EACH ROW RECORDS, because a row's
     reason is read next to that row and this is a statement about the WEEK. */
  const painful = [...input.painfulRegions].sort();
  if (painful.length > 0) {
    const touched = new Set<string>();
    for (const plan of planned) {
      const facts = plan.introducedFacts ?? byId.get(plan.sourceRowId)?.facts;
      if (facts === undefined) continue;
      if (facts.painRegions.some((r) => input.painfulRegions.includes(r))) touched.add(facts.name);
    }
    if (touched.size > 0) {
      out.push({
        kind: "painAlert",
        message: `You reported pain in ${painful.join(", ")}. ${touched.size} exercise(s) that `
          + "train it are held where they are until it settles. If it is not settling, that is "
          + "worth a professional's eyes rather than another week of training around it.",
      });
    }
  }
  return out;
}

/* ════════════════════════════════════════════════════════════════════════════════════════════
 * SLICE 13 — §4, THE STRAIGHT-ARM BUDGET. The LAST pass, and the only one that TAKES SOMETHING
 * BACK. Mirrors V6Progression.swift:1885–1975.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** Whether a row's seconds are COUNTED by the budget. Mirrors `V6Progression.isBudgeted`,
 *  V6Progression.swift:1906.
 *
 *  A straight-arm HOLD, in a MAIN section, that is not a marker. ⚠️ THE DEFINITION IS PINNED BY
 *  THE SPEC'S OWN ARITHMETIC — week 1 is 30+12+12+20+24+14+24 = 136 s exactly — which is what
 *  proves warm-up leans and the Saturday handstand MARKER are outside it, and that a straight-arm
 *  row prescribed in REPS (the tuck front-lever pull) contributes nothing.
 *
 *  🔴 A RETEST IS EXCLUDED ALONGSIDE THE MARKER, and the reason pins the budget's meaning: the
 *  allowance is an accumulated-TUT constraint on SUBMAXIMAL work, and §5.13's max test is a single
 *  MAXIMAL effort whose length is unknown until it has been made. Counting it would make a test
 *  week read as over budget BY CONSTRUCTION — on a deload allowance of 60% — and the refusal
 *  machinery would then start taking sets away from the measurement itself. */
export function isBudgeted(
  facts: ExerciseFacts,
  role: RuleRow["sectionRole"],
  prescription: RuleRowPrescription,
): boolean {
  if (!facts.isStraightArm) return false;
  if (role !== "main") return false;
  if (prescription.holdSecondsMin === null) return false;
  const config = RowConfig.from(prescription.progressionConfig as Json | undefined);
  if (config === null) return true;
  if (config.rule === "marker") return false;
  return config.bool("test_max") !== true;
}

/** The week's prescribed straight-arm seconds. Mirrors `straightArmSeconds`,
 *  V6Progression.swift:1916. */
export function straightArmSeconds(rows: RuleRow[], planned: PlannedRow[]): number {
  const byId = new Map(rows.map((r) => [r.id, r] as const));
  let total = 0;
  for (const plan of planned) {
    /* ⚠️ `introducedFacts` FIRST. A promoted rung's `sourceRowId` is its PARENT's, so looking the
       facts up by that id alone would price a straddle planche as the tuck planche it grew out of
       — and the two legitimately differ on `isStraightArm`. */
    const facts = plan.introducedFacts ?? byId.get(plan.sourceRowId)?.facts;
    /* An introduced row sits in its PARENT's section, so the role is the parent's either way —
       which is also why a promotion cannot smuggle main-role holds into a warm-up. */
    const role = byId.get(plan.sourceRowId)?.sectionRole ?? null;
    if (facts === undefined) continue;
    if (!isBudgeted(facts, role, plan.prescription)) continue;
    total += plan.prescription.sets * (plan.prescription.holdSecondsMin ?? 0);
  }
  return total;
}

/** §4 — D-V6.7, REFUSE THEN FLAG. Mirrors `V6Progression.enforceBudget`,
 *  V6Progression.swift:1936.
 *
 *  🔴 AN ADDITION **THIS WEEK** THAT BREAKS THE ALLOWANCE IS TAKEN BACK, LAST GRANTED FIRST; AN
 *  OVERRUN THE WEEK **INHERITED** CANNOT BE FIXED BY TAKING BACK NOTHING, so it is REPORTED rather
 *  than silently tolerated. Those are two different situations and collapsing them would either
 *  strip sets a human deliberately wrote, or pass a week that is over its allowance in silence. */
export function enforceBudget(
  input: RuleInput,
  planned: PlannedRow[],
  changes: Change[],
  additions: string[],
): { planned: PlannedRow[]; changes: Change[]; verdict: BudgetVerdict } {
  let out = [...planned];
  let outChanges = [...changes];
  const allowance = input.config.straightArmAllowanceSeconds(input.weekNumber);
  if (allowance === null) {
    /* ⚠️ `notConfigured` IS REPORTED, NOT TREATED AS A PASS. A program with no budget has not
       satisfied a constraint; it has not been given one, and the two read differently to anybody
       looking at the verdict. */
    return {
      planned: out, changes: outChanges,
      verdict: {
        state: "notConfigured", allowanceSeconds: null,
        prescribedSeconds: straightArmSeconds(input.rows, out), refusedRowIds: [],
      },
    };
  }

  const byId = new Map(input.rows.map((r) => [r.id, r] as const));
  const refused: string[] = [];
  const pending = [...additions];
  /* A hair of tolerance so 149.6 is not beaten by 149.60000000000002. ⚠️ The SAME epsilon as the
     Swift's, because a twin with a different tolerance would refuse a set on one platform and
     allow it on the other for a week that sits exactly on the line. */
  const epsilon = 1e-6;
  while (straightArmSeconds(input.rows, out) > allowance + epsilon) {
    const rowId = pending.pop();
    if (rowId === undefined) break;
    const index = out.findIndex((p) => p.sourceRowId === rowId);
    const row = byId.get(rowId);
    if (index < 0 || row === undefined) continue;
    if (out[index]!.prescription.sets <= 1) continue;
    const prescription = { ...out[index]!.prescription };
    setSets(prescription, prescription.sets - 1);
    const reason = `${row.facts.name}: the extra set was taken back — it would have put the `
      + `week's straight-arm holds over their ${halfToEven(allowance)} s budget.`;
    prescription.selectionReason = reason;
    /* ⚠️ THE ROW GOES BACK AS A PLAIN PLANNED ROW, `introducedFacts` CLEARED — mirroring the
       Swift, whose `PlannedRow(sourceRowId:prescription:)` defaults it to nil. Said out loud
       because §4 can only ever reach a row that GAINED a set this week, and §5.5's introduced
       rows enter at one set and are not in `additions`. */
    out[index] = { sourceRowId: rowId, prescription, introducedFacts: null };
    /* The `setAdded` change is REMOVED, not contradicted: leaving it beside the refusal would
       tell the athlete a set was granted and taken away in the same breath. */
    outChanges = outChanges.filter((c) => !(c.rowId === rowId && c.kind === "setAdded"));
    outChanges.push({ rowId, exerciseName: row.facts.name, kind: "refusedByBudget", reason });
    refused.push(rowId);
  }

  const total = straightArmSeconds(input.rows, out);
  const state: BudgetVerdict["state"] = total > allowance + epsilon
    ? "overBudget"
    : (refused.length === 0 ? "withinBudget" : "refusedAnAddition");
  return {
    planned: out, changes: outChanges,
    verdict: { state, allowanceSeconds: allowance, prescribedSeconds: total, refusedRowIds: refused },
  };
}

/* ════════════════════════════════════════════════════════════════════════════════════════════
 * SLICE 14 — THE LAST TWO: §5.4's DOSING BAND AS A CHECK. (§5.7's introduction ramp is ten lines
 * and lives in the row loop where it belongs, beside the rule whose set count it shares.)
 *
 * 🔴 AFTER THIS, `portGaps` REFUSES NOTHING.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** Mirrors `V6Progression.doseBandNote`, V6Progression.swift:1983.
 *
 *  🔴 IT **REPORTS** AND DOES NOT RE-DOSE, and that is the whole of the design rather than a
 *  limitation. The stronger rule beside it is §5.4's: hold seconds NEVER LENGTHEN inside a rung.
 *  A row prescribed outside the 60–75% band is a row whose rung is wrong, and only a TEST WEEK
 *  (§5.13's `redose`) may end a rung — so this says so, in the warnings, where a person reads it.
 *
 *  ⚠️ IT READS THE MAXIMA THE **CALLER** HOLDS, not the ones this week just measured. That is why
 *  it is a different function from `redose` and why `testedMaxSeconds` stayed a gap in `portGaps`
 *  from §5.13 until now: a caller passing the map would have had it silently ignored. */
export function doseBandNote(
  row: RuleRow,
  prescription: RuleRowPrescription,
  testedMaxSeconds: Record<string, number>,
): string | null {
  const seconds = prescription.holdSecondsMin;
  if (seconds === null || seconds <= 0) return null;
  const maximum = testedMaxSeconds[row.facts.id];
  if (maximum === undefined || maximum <= 0) return null;
  const share = seconds / maximum;
  /* ⚠️ OUTSIDE MEANS EITHER SIDE. A hold that is too SHORT for the tested max is as much a wrong
     rung as one that is too long, and reporting only the overshoot would let a rung the athlete
     has outgrown sit there unremarked. */
  if (share >= DOSE_BAND_LOW && share <= DOSE_BAND_HIGH) return null;
  return `${row.facts.name} is prescribed ${seconds}s against a tested max of `
    + `${halfToEven(maximum)}s (${halfToEven(share * 100)}%), outside the `
    + `${halfToEven(DOSE_BAND_LOW * 100)}–${halfToEven(DOSE_BAND_HIGH * 100)}% band. `
    + "Hold seconds are not changed here — the test week re-derives them.";
}
